/**
 * BoardDoc — the typed accessor layer over a Yjs board document.
 *
 * Invariant (docs/08 §2): ALL doc access goes through this class; feature code
 * never touches raw Y types. The concurrency rules from docs/07 are enforced
 * here in one place:
 *
 *  - C1: order = fractional `ord` keys; a move is a SINGLE ord write; reads
 *    sort by `(ord, id)`. No ordered arrays exist anywhere.
 *  - C2: one CRDT key per independently-editable property (`v`/`st` + one key
 *    per FORMAT property on cells — the whole-object `fmt` key is retired, see
 *    schema.ts; `x`/`y`/`w`/`h`/`z`/`rotation` on elements).
 *  - C3: `permanentReorder` rewrites all ord keys in ONE transaction.
 *  - C4: deleting a row/column removes only its meta entry; stranded cell
 *    writes become orphaned entries swept later by snapshot compaction.
 *
 * F-28 ("Original Script"): the locked source column's rename/delete/move
 * refusals live HERE, not in the UI — `setColumnName`, `deleteColumn` and
 * `moveColumn` return false having written nothing, and `addColumn` clamps an
 * insert that would land left of it. The client's `column-lock.ts` only decides
 * which affordances to OFFER; this is the enforcement.
 *
 * Every mutator takes an optional `origin` and runs inside
 * `doc.transact(fn, origin)` — origins drive Y.UndoManager `trackedOrigins`
 * (C6/C8) and let the server distinguish worker writes from human ones.
 * Refusals are decided BEFORE opening a transaction: a refusal that emitted an
 * empty transaction would still be a no-op, but deciding outside keeps "refused
 * ⇒ not one byte of doc change" true by construction (see source-column.test).
 */
import * as Y from "yjs";
import { cellElementExtent, colElementWidth, planCellFit, planCellRatchet, rowElementHeight, } from "./element-cell.js";
import { rebaseTextMarks, sanitizeTextMarks, TEXT_MARK_KINDS, } from "../text-marks.js";
import { rebaseTextTypeSwitches, sanitizeTextTypeSwitches, } from "../text-types.js";
import { keyAtIndex, planInsertAtIndex, planInsertManyAtIndex, rebalance, sortOrdered, } from "./order.js";
import { ART_DIRECTION_TEXT_FIELDS, artDirectionKey, CELL_FORMAT_PROPS, cellMarkProp, cellPropKey, clipRangeOf, DESIGN_BRIEF_KEY, DESIGN_JOB_KEY, DESIGN_MODEL_KEY, DESIGN_PALETTE_KEY, DESIGN_UPDATED_AT_KEY, getCells, getColMeta, getComments, getConnectors, getDesign, getDesignRefs, getDesignShots, getElements, getThumbPortraits, getThumbs, getThumbShots, getMeta, getRowMeta, getSheets, isColumnTranslation, isConnectorEndpoint, bgmCellText, bgmStemsOf, isBgmCellValue, isImageCellValue, isDesignJobState, isPalette, isThumbJobState, isTranslationJobState, legacyCellFmtKey, MAX_PALETTE_SWATCHES, parseCellPropKey, THUMB_IMAGE_MODEL_KEY, THUMB_JOB_KEY, THUMB_PORTRAIT_LABEL_MAX_LENGTH, THUMB_SELECTED_KEY, THUMB_TEXT_KEY, THUMB_TEXT_MAX_LENGTH, THUMB_TEXT_MODEL_KEY, THUMB_TITLE_KEY, THUMB_TITLE_MAX_LENGTH, TRANSLATION_JOB_KEY, } from "./schema.js";
export const DEFAULT_ROW_HEIGHT = 28;
export const DEFAULT_COLUMN_WIDTH = 120;
export const DEFAULT_COLUMN_TYPE = "text";
export class BoardDoc {
    doc;
    /**
     * NOTE: the constructor deliberately does NOT stamp `meta.schemaVersion`.
     * Clients construct a BoardDoc on an EMPTY local doc before first sync; a
     * constructor-time stamp would be causally concurrent with whatever the
     * server doc holds, and the Yjs map-key LWW (clientID order) can then
     * downgrade a future HIGHER schemaVersion (~coin flip per open) — defeating
     * the docs/08 §2 non-additive schema gate. The stamp happens server-side
     * only, in Hocuspocus onLoadDocument (after loading stored state), via
     * `initBoardDoc`.
     */
    constructor(doc = new Y.Doc()) {
        this.doc = doc;
    }
    /**
     * Run `fn` in a single Yjs transaction with the given origin. Nested calls
     * (mutators invoked inside an outer `transact`) join the outer transaction,
     * so composite operations are one sync unit and one undo step.
     */
    transact(fn, origin) {
        let result;
        this.doc.transact(() => {
            result = fn();
        }, origin ?? null);
        return result;
    }
    /* ---------------------------------------------------------------- */
    /* Meta                                                              */
    /* ---------------------------------------------------------------- */
    get schemaVersion() {
        return this.metaMap().get("schemaVersion") ?? 0;
    }
    getBoardName() {
        return this.metaMap().get("name") ?? "";
    }
    setBoardName(name, origin) {
        this.transact(() => {
            this.metaMap().set("name", name);
        }, origin);
    }
    /* ---------------------------------------------------------------- */
    /* Sheets                                                            */
    /* ---------------------------------------------------------------- */
    /** Create a sheet appended after the current last one. No-op if it exists. */
    createSheet(sheetId, name, origin) {
        this.transact(() => {
            const sheets = getSheets(this.doc);
            if (sheets.has(sheetId))
                return;
            const ordered = this.orderedEntries(sheets);
            const ord = keyAtIndex(ordered.map((e) => e.ord), ordered.length);
            const entry = new Y.Map();
            entry.set("name", name);
            entry.set("ord", ord);
            sheets.set(sheetId, entry);
        }, origin);
    }
    /** All sheets sorted by `(ord, id)`. */
    listSheets() {
        const sheets = getSheets(this.doc);
        const out = [];
        sheets.forEach((entry, id) => {
            out.push({
                id,
                name: entry.get("name") ?? "",
                ord: entry.get("ord") ?? "",
            });
        });
        return sortOrdered(out).map(({ id, name, ord }) => ({ id, name, ord }));
    }
    renameSheet(sheetId, name, origin) {
        this.transact(() => {
            getSheets(this.doc).get(sheetId)?.set("name", name);
        }, origin);
    }
    /* ---------------------------------------------------------------- */
    /* Rows                                                              */
    /* ---------------------------------------------------------------- */
    /**
     * Insert a row: ONE new rowMeta entry with an ord between its neighbors.
     * (When the insertion point falls inside a run of EQUAL ord keys — possible
     * after concurrent same-gap inserts — the run tail is re-keyed in the same
     * transaction so the row lands exactly at `index`; see `planInsertAtIndex`.)
     */
    addRow(sheetId, rowId, options = {}) {
        this.transact(() => {
            const rowMeta = getRowMeta(this.doc, sheetId);
            if (rowMeta.has(rowId))
                return;
            const ordered = this.orderedEntries(rowMeta);
            const index = options.index ?? ordered.length;
            const plan = planInsertAtIndex(ordered, index);
            for (const rewrite of plan.rewrites) {
                rowMeta.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            const entry = new Y.Map();
            entry.set("ord", plan.ord);
            entry.set("height", options.height ?? DEFAULT_ROW_HEIGHT);
            rowMeta.set(rowId, entry);
        }, options.origin);
    }
    /**
     * F-90 BULK insert: `rowIds.length` rows in ONE pass — one read of the
     * current order, one `generateNKeysBetween` call for the whole batch, one
     * transaction (one sync unit, one undo step).
     *
     * This exists because `addRow` in a loop is O(n²): each call re-reads and
     * re-sorts every existing row to find its neighbors (measured 2026-07-16:
     * 5/8/25/93 ms at N=250/500/1k/2k — ~1 min at the 50k ceiling). Importing a
     * 10k-row script (F-90) MUST use this path. Single inserts stay on `addRow`.
     *
     * Ordering contract: the batch lands at `index` in the given order, with
     * every row strictly between the same neighbors — so an import preserves the
     * source file's line order (the whole point of a script import).
     *
     * Ids already present, and duplicates WITHIN `rowIds`, are skipped — the
     * batch's key count is computed after that filter, so no ord key is ever
     * minted for a row that is not written. Returns the ids actually added, in
     * order (a caller driving a progress bar gets the real count).
     *
     * F-90 asks for BOUNDED transactions (~500 rows each) for a big import;
     * that chunking is the caller's (the importer's) call — this accessor
     * commits exactly what it is handed.
     */
    addRows(sheetId, rowIds, options = {}) {
        return this.transact(() => {
            const rowMeta = getRowMeta(this.doc, sheetId);
            const fresh = [];
            const seen = new Set();
            for (const rowId of rowIds) {
                if (seen.has(rowId) || rowMeta.has(rowId))
                    continue;
                seen.add(rowId);
                fresh.push(rowId);
            }
            if (fresh.length === 0)
                return [];
            const ordered = this.orderedEntries(rowMeta);
            const plan = planInsertManyAtIndex(ordered, options.index ?? ordered.length, fresh.length);
            for (const rewrite of plan.rewrites) {
                rowMeta.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            const height = options.height ?? DEFAULT_ROW_HEIGHT;
            for (let i = 0; i < fresh.length; i++) {
                const entry = new Y.Map();
                entry.set("ord", plan.ords[i]);
                entry.set("height", height);
                rowMeta.set(fresh[i], entry);
            }
            return fresh;
        }, options.origin);
    }
    /**
     * Delete a row = remove its rowMeta entry only (C4: deletion wins; cell
     * entries orphan harmlessly and are swept by snapshot compaction).
     */
    deleteRow(sheetId, rowId, origin) {
        this.transact(() => {
            getRowMeta(this.doc, sheetId).delete(rowId);
        }, origin);
    }
    /**
     * Delete several rows in ONE transaction (one sync unit, one undo step) —
     * the multi-row-select "Delete N rows" context-menu action. Unlike `addRows`
     * there is no ord math to batch: each delete is just a `rowMeta.delete`
     * (C4), so this is a straight loop rather than needing its own O(n) planner.
     * Ids not present in `rowMeta` are silently skipped (already gone, e.g. a
     * concurrent delete landed first). Returns the ids actually removed.
     */
    deleteRows(sheetId, rowIds, origin) {
        const rowMeta = getRowMeta(this.doc, sheetId);
        const doomed = Array.from(new Set(rowIds)).filter((id) => rowMeta.has(id));
        if (doomed.length === 0)
            return [];
        this.transact(() => {
            for (const id of doomed)
                rowMeta.delete(id);
        }, origin);
        return doomed;
    }
    /**
     * Move a row to `index` (position among the OTHER rows, 0..count-1).
     * C1: exactly ONE LWW write to the row's `ord` key — concurrent moves of the
     * same row converge to one position; duplication is structurally impossible.
     */
    moveRow(sheetId, rowId, index, origin) {
        this.transact(() => {
            const rowMeta = getRowMeta(this.doc, sheetId);
            const entry = rowMeta.get(rowId);
            if (!entry)
                return;
            const others = this.orderedEntries(rowMeta).filter((e) => e.id !== rowId);
            const plan = planInsertAtIndex(others, index);
            for (const rewrite of plan.rewrites) {
                rowMeta.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            entry.set("ord", plan.ord);
        }, origin);
    }
    hasRow(sheetId, rowId) {
        return getRowMeta(this.doc, sheetId).has(rowId);
    }
    getRow(sheetId, rowId) {
        const entry = getRowMeta(this.doc, sheetId).get(rowId);
        if (!entry)
            return undefined;
        return {
            id: rowId,
            height: entry.get("height") ?? DEFAULT_ROW_HEIGHT,
            ord: entry.get("ord") ?? "",
        };
    }
    setRowHeight(sheetId, rowId, height, origin) {
        this.transact(() => {
            getRowMeta(this.doc, sheetId).get(rowId)?.set("height", height);
        }, origin);
    }
    /** Row ids sorted by `(ord, id)` — the shared ground-truth order (C1). */
    sortedRowIds(sheetId) {
        return this.orderedEntries(getRowMeta(this.doc, sheetId)).map((e) => e.id);
    }
    /* ---------------------------------------------------------------- */
    /* Columns                                                           */
    /* ---------------------------------------------------------------- */
    /**
     * Insert a column. F-28: an `index` at or before the locked source column is
     * CLAMPED to its right — the source column stays leftmost no matter which
     * path asks. (The UI withholds that affordance instead of relying on the
     * clamp; see the client's `column-lock.ts` note. The clamp is the backstop
     * for a racing insert.)
     */
    addColumn(sheetId, colId, options = {}) {
        this.transact(() => {
            const colMeta = getColMeta(this.doc, sheetId);
            if (colMeta.has(colId))
                return;
            const ordered = this.orderedEntries(colMeta);
            const requested = options.index ?? ordered.length;
            const index = this.clampColumnIndex(sheetId, ordered, requested);
            const plan = planInsertAtIndex(ordered, index);
            for (const rewrite of plan.rewrites) {
                colMeta.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            const entry = new Y.Map();
            entry.set("name", options.name ?? "");
            entry.set("type", options.type ?? DEFAULT_COLUMN_TYPE);
            entry.set("width", options.width ?? DEFAULT_COLUMN_WIDTH);
            entry.set("ord", plan.ord);
            entry.set("frozen", options.frozen ?? false);
            // F-28: absent = unlocked. Only the seeded source column carries the key,
            // so every pre-F-28 board reads exactly as before (nothing enforced).
            if (options.locked === true)
                entry.set("locked", true);
            colMeta.set(colId, entry);
        }, options.origin);
    }
    /**
     * F-90 BULK column insert — the `addRows` contract, for columns: one read of
     * the current order, one key generation for the batch, one transaction, and
     * the same skip-existing/skip-duplicate filter. Returns the ids added.
     *
     * F-28: the batch's `index` is CLAMPED past the locked source column exactly
     * once (the clamp depends only on the pre-batch order), so no bulk path can
     * displace it from leftmost. `ColumnInit` deliberately has no `locked` field:
     * the source column is write-once at seed time, and letting a bulk import
     * mint a second locked column would make `sourceColumnId` ambiguous.
     */
    addColumns(sheetId, columns, options = {}) {
        return this.transact(() => {
            const colMeta = getColMeta(this.doc, sheetId);
            const fresh = [];
            const seen = new Set();
            for (const column of columns) {
                if (seen.has(column.id) || colMeta.has(column.id))
                    continue;
                seen.add(column.id);
                fresh.push(column);
            }
            if (fresh.length === 0)
                return [];
            const ordered = this.orderedEntries(colMeta);
            const requested = options.index ?? ordered.length;
            const index = this.clampColumnIndex(sheetId, ordered, requested);
            const plan = planInsertManyAtIndex(ordered, index, fresh.length);
            for (const rewrite of plan.rewrites) {
                colMeta.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            for (let i = 0; i < fresh.length; i++) {
                const column = fresh[i];
                const entry = new Y.Map();
                entry.set("name", column.name ?? "");
                entry.set("type", column.type ?? DEFAULT_COLUMN_TYPE);
                entry.set("width", column.width ?? DEFAULT_COLUMN_WIDTH);
                entry.set("ord", plan.ords[i]);
                entry.set("frozen", column.frozen ?? false);
                colMeta.set(column.id, entry);
            }
            return fresh.map((column) => column.id);
        }, options.origin);
    }
    /**
     * Delete a column = remove its colMeta entry only (C4, same as rows).
     * Returns false — having written NOTHING — for the F-28 locked source column
     * or a column that is already gone.
     */
    deleteColumn(sheetId, colId, origin) {
        const colMeta = getColMeta(this.doc, sheetId);
        if (!colMeta.has(colId))
            return false;
        if (this.isColumnLocked(sheetId, colId))
            return false; // F-28
        this.transact(() => {
            colMeta.delete(colId);
        }, origin);
        return true;
    }
    /**
     * Move a column to `index` (position among the OTHER columns) — single `ord`
     * write, same contract as `moveRow` (C1). Returns false, writing NOTHING,
     * when F-28 forbids the move: the locked source column never moves, and no
     * other column may land at or before it.
     */
    moveColumn(sheetId, colId, index, origin) {
        const colMeta = getColMeta(this.doc, sheetId);
        const entry = colMeta.get(colId);
        if (!entry)
            return false;
        if (this.isColumnLocked(sheetId, colId))
            return false; // F-28: it never moves
        const others = this.orderedEntries(colMeta).filter((e) => e.id !== colId);
        const sourceColId = this.sourceColumnId(sheetId);
        if (sourceColId !== undefined) {
            // `index` is a position among `others`, which still contains the source:
            // landing at or before its slot would displace it from leftmost.
            const sourceIndex = others.findIndex((e) => e.id === sourceColId);
            if (sourceIndex >= 0 && index <= sourceIndex)
                return false;
        }
        this.transact(() => {
            const plan = planInsertAtIndex(others, index);
            for (const rewrite of plan.rewrites) {
                colMeta.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            entry.set("ord", plan.ord);
        }, origin);
        return true;
    }
    hasColumn(sheetId, colId) {
        return getColMeta(this.doc, sheetId).has(colId);
    }
    getColumn(sheetId, colId) {
        const entry = getColMeta(this.doc, sheetId).get(colId);
        if (!entry)
            return undefined;
        return {
            id: colId,
            name: entry.get("name") ?? "",
            type: entry.get("type") ?? DEFAULT_COLUMN_TYPE,
            width: entry.get("width") ?? DEFAULT_COLUMN_WIDTH,
            ord: entry.get("ord") ?? "",
            frozen: entry.get("frozen") ?? false,
            locked: entry.get("locked") === true,
        };
    }
    /**
     * F-28: id of the board's locked "Original Script" column — the canonical
     * translation source (F-70), resolved by the `locked` key rather than by
     * name or position (both of which the accessors then pin in place).
     * `undefined` on a board seeded before F-28: nothing is locked, nothing is
     * enforced, and the F-70 entry point stays hidden.
     */
    sourceColumnId(sheetId) {
        const colMeta = getColMeta(this.doc, sheetId);
        for (const colId of this.sortedColIds(sheetId)) {
            if (colMeta.get(colId)?.get("locked") === true)
                return colId;
        }
        return undefined;
    }
    /** F-28: is this the locked source column? False for every legacy column. */
    isColumnLocked(sheetId, colId) {
        return getColMeta(this.doc, sheetId).get(colId)?.get("locked") === true;
    }
    /**
     * F-23/F-12b rename. Returns false — having written NOTHING — for the F-28
     * locked source column ("Original Script" is canonical, so its name is not
     * a user's to change) or a column deleted concurrently (C4).
     */
    setColumnName(sheetId, colId, name, origin) {
        const entry = getColMeta(this.doc, sheetId).get(colId);
        if (!entry)
            return false;
        if (this.isColumnLocked(sheetId, colId))
            return false; // F-28
        this.transact(() => {
            entry.set("name", name);
        }, origin);
        return true;
    }
    setColumnWidth(sheetId, colId, width, origin) {
        this.setColumnKey(sheetId, colId, "width", width, origin);
    }
    setColumnType(sheetId, colId, type, origin) {
        this.setColumnKey(sheetId, colId, "type", type, origin);
    }
    setColumnFrozen(sheetId, colId, frozen, origin) {
        this.setColumnKey(sheetId, colId, "frozen", frozen, origin);
    }
    /** Column ids sorted by `(ord, id)` (C1). */
    sortedColIds(sheetId) {
        return this.orderedEntries(getColMeta(this.doc, sheetId)).map((e) => e.id);
    }
    /* ---------------------------------------------------------------- */
    /* F-70 translation mapping (colMeta `tx` — one whole-object key)     */
    /* ---------------------------------------------------------------- */
    /**
     * Set (or clear, with `undefined`) a target column's F-70 translation
     * mapping. ONE whole-object `tx` key — a single semantic unit written by one
     * dialog submit, independent of `name`/`width`/`ord` (C2), so a mapping and a
     * concurrent rename both survive.
     *
     * Returns false, writing NOTHING, for the F-28 locked source column (it is
     * never a target — translating it into itself would destroy the canonical
     * script) or a column deleted concurrently (C4). Clearing an absent mapping
     * is an idempotent success. The stored object is a normalized COPY: keeping
     * the caller's reference would let later local mutation silently fork state.
     */
    setColumnTranslation(sheetId, colId, tx, origin) {
        const entry = getColMeta(this.doc, sheetId).get(colId);
        if (!entry)
            return false; // C4
        if (this.isColumnLocked(sheetId, colId))
            return false; // F-28
        if (tx === undefined) {
            if (!entry.has("tx"))
                return true; // idempotent clear
            this.transact(() => {
                entry.delete("tx");
            }, origin);
            return true;
        }
        const stored = { from: tx.from, to: tx.to, updatedAt: tx.updatedAt };
        if (tx.model !== undefined)
            stored.model = tx.model;
        this.transact(() => {
            entry.set("tx", stored);
        }, origin);
        return true;
    }
    /**
     * Read a column's F-70 mapping. A stored value that does not satisfy
     * `isColumnTranslation` (a future/foreign writer, a hand-edited doc) reads as
     * `undefined` rather than being handed to the translation worker as a
     * half-valid request. The result is a fresh copy — mutating it cannot write
     * back into the doc.
     */
    getColumnTranslation(sheetId, colId) {
        const raw = getColMeta(this.doc, sheetId).get(colId)?.get("tx");
        if (!isColumnTranslation(raw))
            return undefined;
        const out = { from: raw.from, to: raw.to, updatedAt: raw.updatedAt };
        if (raw.model !== undefined)
            out.model = raw.model;
        return out;
    }
    /* ---------------------------------------------------------------- */
    /* F-70 translation JOB state (meta `txJob` — one whole-object key)   */
    /* ---------------------------------------------------------------- */
    /**
     * Publish (or clear, with `undefined`) the live translation-job state — ONE
     * whole-object `txJob` key on `meta`, so every client sees the same progress
     * bar with no polling and a mid-job reload still shows it (F-70, 2026-07-16).
     *
     * SERVER-ONLY, under a DEDICATED origin (`SERVER_ORIGIN`) — never a user
     * origin, or a progress tick would enter that user's undo stack (C6/C8; see
     * the schema.ts writer-discipline note). Clients read via
     * `getTranslationJob`/`observeTranslationJob` and start/cancel through the
     * job API.
     *
     * The stored object is a normalized COPY: keeping the caller's reference
     * would let the job loop's later mutation of its own progress object fork
     * doc state without a transaction (and never broadcast — Yjs cannot see an
     * in-place mutation of a stored plain object). Clearing an absent state is an
     * idempotent no-op.
     */
    setTranslationJob(state, origin) {
        const meta = this.metaMap();
        if (state === undefined) {
            if (!meta.has(TRANSLATION_JOB_KEY))
                return; // idempotent clear
            this.transact(() => {
                meta.delete(TRANSLATION_JOB_KEY);
            }, origin);
            return;
        }
        const stored = {
            jobId: state.jobId,
            sheetId: state.sheetId,
            targetColId: state.targetColId,
            status: state.status,
            done: state.done,
            total: state.total,
            failed: state.failed,
            startedAt: state.startedAt,
        };
        if (state.message !== undefined)
            stored.message = state.message;
        this.transact(() => {
            meta.set(TRANSLATION_JOB_KEY, stored);
        }, origin);
    }
    /**
     * The board's live translation-job state, or `undefined` when there is none.
     * A stored value that fails `isTranslationJobState` (a future writer, a
     * hand-edited doc) reads as `undefined` rather than driving a progress bar
     * from garbage. The result is a fresh copy — mutating it cannot write back.
     */
    getTranslationJob() {
        const raw = this.metaMap().get(TRANSLATION_JOB_KEY);
        if (!isTranslationJobState(raw))
            return undefined;
        const out = {
            jobId: raw.jobId,
            sheetId: raw.sheetId,
            targetColId: raw.targetColId,
            status: raw.status,
            done: raw.done,
            total: raw.total,
            failed: raw.failed,
            startedAt: raw.startedAt,
        };
        if (raw.message !== undefined)
            out.message = raw.message;
        return out;
    }
    /**
     * Subscribe to job-state changes — the client's live progress feed. Fires
     * only when the `txJob` key itself changed (other `meta` writes, e.g. a board
     * rename, are filtered out), with the current state or `undefined` when it
     * was cleared. Returns an unsubscribe function.
     */
    observeTranslationJob(listener) {
        const meta = this.metaMap();
        const observer = (event) => {
            if (!event.keysChanged.has(TRANSLATION_JOB_KEY))
                return;
            listener(this.getTranslationJob());
        };
        meta.observe(observer);
        return () => meta.unobserve(observer);
    }
    /* ---------------------------------------------------------------- */
    /* Design theme (`design` root map)                                  */
    /* ---------------------------------------------------------------- */
    /**
     * The user's standing instruction for the design generator. THEIRS, not the
     * model's: the generator only ever reads this key, so a regeneration can
     * never quietly rewrite the brief it was given.
     *
     * No `at` — the brief is not part of the art direction, so editing it does
     * not make the generated theme any newer than it was.
     */
    setDesignBrief(brief, origin) {
        const design = this.designMap();
        this.transact(() => {
            design.set(DESIGN_BRIEF_KEY, brief);
        }, origin);
    }
    getDesignBrief() {
        const raw = this.designMap().get(DESIGN_BRIEF_KEY);
        return typeof raw === "string" ? raw : "";
    }
    /**
     * Edit ONE art-direction field. This is the hand-editing path — the panel's
     * text areas call it per field, so two people sharpening different fields
     * never overwrite each other (C2).
     *
     * Stamps `ad:updatedAt` but NOT `ad:model`: a human edit is no longer that
     * model's output, and clearing the provenance on every keystroke would lose
     * "this started as gemini-3.1-flash-lite" the moment anyone fixed a typo.
     */
    setArtDirectionField(field, text, options) {
        const design = this.designMap();
        this.transact(() => {
            design.set(artDirectionKey(field), text);
            design.set(DESIGN_UPDATED_AT_KEY, options.at);
        }, options.origin);
    }
    /**
     * Replace the palette — ONE whole-object key (schema.ts: a palette is chosen
     * as a set). Hexes are normalized to lowercase `#rrggbb` and the list is
     * capped at `MAX_PALETTE_SWATCHES`; anything that is not a hex colour is
     * dropped rather than stored for the renderer to choke on.
     */
    setDesignPalette(swatches, options) {
        const design = this.designMap();
        const stored = normalizePalette(swatches);
        this.transact(() => {
            design.set(DESIGN_PALETTE_KEY, stored);
            design.set(DESIGN_UPDATED_AT_KEY, options.at);
        }, options.origin);
    }
    /**
     * Apply a WHOLE generated art direction — every field plus the palette and
     * provenance — in ONE transaction, so a generation is one sync unit and one
     * undo step (Ctrl+Z puts the previous art direction back intact, rather than
     * peeling off one field at a time).
     *
     * Called by the CLIENT with a user origin, not by the server: the design
     * route is stateless and returns the art direction for the person who asked
     * to apply it (docs/08 §4's `writeOutput` is deliberately not used here —
     * there is no C5 guard to satisfy when the write is a human's own edit).
     */
    setArtDirection(artDirection, options) {
        const design = this.designMap();
        this.transact(() => {
            for (const field of ART_DIRECTION_TEXT_FIELDS) {
                design.set(artDirectionKey(field), artDirection[field]);
            }
            design.set(DESIGN_PALETTE_KEY, normalizePalette(artDirection.palette));
            if (options.model !== undefined)
                design.set(DESIGN_MODEL_KEY, options.model);
            design.set(DESIGN_UPDATED_AT_KEY, options.at);
        }, options.origin);
    }
    /**
     * The board's art direction, with every absent field reading as `""` — the
     * panel renders empty fields rather than branching on "has a theme yet", and
     * `hasArtDirection()` answers that question separately.
     */
    getArtDirection() {
        const design = this.designMap();
        const text = (field) => {
            const raw = design.get(artDirectionKey(field));
            return typeof raw === "string" ? raw : "";
        };
        const palette = design.get(DESIGN_PALETTE_KEY);
        return {
            logline: text("logline"),
            mood: text("mood"),
            perspective: text("perspective"),
            lighting: text("lighting"),
            texture: text("texture"),
            motion: text("motion"),
            type: text("type"),
            avoid: text("avoid"),
            // Fresh copy: mutating the read must not write back into the doc.
            palette: isPalette(palette) ? palette.map((s) => ({ hex: s.hex, name: s.name })) : [],
        };
    }
    /** Has anyone generated or written a theme yet? Drives the panel's empty state. */
    hasArtDirection() {
        const design = this.designMap();
        if (isPalette(design.get(DESIGN_PALETTE_KEY)))
            return true;
        return ART_DIRECTION_TEXT_FIELDS.some((field) => {
            const raw = design.get(artDirectionKey(field));
            return typeof raw === "string" && raw.trim().length > 0;
        });
    }
    /** Model that produced the current art direction, if it came from one. */
    getDesignModel() {
        const raw = this.designMap().get(DESIGN_MODEL_KEY);
        return typeof raw === "string" ? raw : undefined;
    }
    /** When the art direction was last written by anyone (ms since epoch). */
    getDesignUpdatedAt() {
        const raw = this.designMap().get(DESIGN_UPDATED_AT_KEY);
        return typeof raw === "number" ? raw : undefined;
    }
    /**
     * Subscribe to any change in the design map — the panel's and the board
     * band's live feed. Unlike `observeTranslationJob` this does NOT filter by
     * key: every key here belongs to the same one surface, so a change to any of
     * them is a change the subscriber has to re-render for. Returns an
     * unsubscribe function.
     */
    observeDesign(listener) {
        const design = this.designMap();
        const observer = () => listener();
        design.observe(observer);
        return () => design.unobserve(observer);
    }
    /* ---------------------------------------------------------------- */
    /* Design reference frames (`design/refs`)                           */
    /* ---------------------------------------------------------------- */
    /**
     * Add a style-reference frame. Nested per-entity map keyed by `refId`, like
     * elements and comment threads, and for the same reason: a ref entry is
     * created exactly once by one client (ids are client-generated), so there is
     * no concurrent lazy-creation race to lose.
     *
     * Appends at the end; `ord` is a fractional key (C1) so a later reorder is a
     * single-key write.
     */
    addDesignRef(refId, init, origin) {
        this.transact(() => {
            const refs = getDesignRefs(this.doc);
            if (refs.has(refId))
                return; // idempotent
            const ord = planInsertAtIndex(this.orderedRefEntries(refs), refs.size).ord;
            const entry = new Y.Map();
            entry.set("assetId", init.assetId); // immutable after creation
            entry.set("w", init.w);
            entry.set("h", init.h);
            entry.set("ord", ord);
            entry.set("note", init.note ?? "");
            refs.set(refId, entry);
        }, origin);
    }
    /** The note on a reference — its own key, independently editable (C2). */
    setDesignRefNote(refId, note, origin) {
        this.transact(() => {
            getDesignRefs(this.doc).get(refId)?.set("note", note);
        }, origin);
    }
    deleteDesignRef(refId, origin) {
        this.transact(() => {
            getDesignRefs(this.doc).delete(refId);
        }, origin);
    }
    /** Every reference frame, in `(ord, id)` order. */
    listDesignRefs() {
        const refs = getDesignRefs(this.doc);
        const out = [];
        for (const { id } of sortOrdered(this.orderedRefEntries(refs))) {
            const entry = refs.get(id);
            if (entry === undefined)
                continue;
            const assetId = entry.get("assetId");
            if (typeof assetId !== "string")
                continue; // half-written / foreign entry
            out.push({
                id,
                assetId,
                w: numberOr(entry.get("w"), 0),
                h: numberOr(entry.get("h"), 0),
                ord: typeof entry.get("ord") === "string" ? entry.get("ord") : "",
                note: typeof entry.get("note") === "string" ? entry.get("note") : "",
            });
        }
        return out;
    }
    /* ---------------------------------------------------------------- */
    /* Generated key visuals (`design/shots`)                            */
    /* ---------------------------------------------------------------- */
    /**
     * Record one generated key visual. SERVER-side, under a machine origin — the
     * job writes these as they land.
     *
     * Never replaces a previous run: shots accumulate and `runId` groups them, so
     * a regeneration the user dislikes has not destroyed the one before it.
     */
    addDesignShot(shotId, init, origin) {
        this.transact(() => {
            const shots = getDesignShots(this.doc);
            if (shots.has(shotId))
                return; // idempotent
            const ord = init.ord ?? planInsertAtIndex(this.orderedShotEntries(shots), shots.size).ord;
            const entry = new Y.Map();
            entry.set("assetId", init.assetId);
            entry.set("w", init.w);
            entry.set("h", init.h);
            entry.set("runId", init.runId);
            entry.set("ord", ord);
            entry.set("prompt", init.prompt);
            entry.set("model", init.model);
            entry.set("createdAt", init.createdAt);
            // Theme element pages only — a per-row shot is named by its row.
            if (init.label !== undefined)
                entry.set("label", init.label);
            if (init.elementId !== undefined)
                entry.set("elementId", init.elementId);
            // Per-row shots only. Absent = a theme key visual.
            if (init.rowId !== undefined)
                entry.set("rowId", init.rowId);
            if (init.colId !== undefined)
                entry.set("colId", init.colId);
            shots.set(shotId, entry);
        }, origin);
    }
    /**
     * Point a shot at the board element showing it (or clear it when the element
     * is removed). Its own key, so re-placing a shot never rewrites its prompt.
     */
    setDesignShotElement(shotId, elementId, origin) {
        this.transact(() => {
            const entry = getDesignShots(this.doc).get(shotId);
            if (entry === undefined)
                return;
            if (elementId === undefined)
                entry.delete("elementId");
            else
                entry.set("elementId", elementId);
        }, origin);
    }
    deleteDesignShot(shotId, origin) {
        this.transact(() => {
            getDesignShots(this.doc).delete(shotId);
        }, origin);
    }
    /** Every generated shot, in `(ord, id)` order. */
    listDesignShots() {
        const shots = getDesignShots(this.doc);
        const out = [];
        for (const { id } of sortOrdered(this.orderedShotEntries(shots))) {
            const entry = shots.get(id);
            if (entry === undefined)
                continue;
            const assetId = entry.get("assetId");
            const runId = entry.get("runId");
            if (typeof assetId !== "string" || typeof runId !== "string")
                continue;
            const shot = {
                id,
                assetId,
                runId,
                w: numberOr(entry.get("w"), 0),
                h: numberOr(entry.get("h"), 0),
                ord: typeof entry.get("ord") === "string" ? entry.get("ord") : "",
                prompt: typeof entry.get("prompt") === "string" ? entry.get("prompt") : "",
                model: typeof entry.get("model") === "string" ? entry.get("model") : "",
                createdAt: numberOr(entry.get("createdAt"), 0),
            };
            const label = entry.get("label");
            if (typeof label === "string")
                shot.label = label;
            const elementId = entry.get("elementId");
            if (typeof elementId === "string")
                shot.elementId = elementId;
            const rowId = entry.get("rowId");
            const colId = entry.get("colId");
            if (typeof rowId === "string")
                shot.rowId = rowId;
            if (typeof colId === "string")
                shot.colId = colId;
            out.push(shot);
        }
        return out;
    }
    /** Shots from one generation run, in order. */
    listDesignShotsInRun(runId) {
        return this.listDesignShots().filter((shot) => shot.runId === runId);
    }
    /**
     * Shots belonging to the WHOLE video — the theme key visuals the board band
     * shows. Excludes per-row designs, which live on their own rows.
     */
    listThemeShots() {
        return this.listDesignShots().filter((shot) => shot.rowId === undefined);
    }
    /** Every per-row design for `rowId`, oldest first. */
    listDesignShotsForRow(rowId) {
        return this.listDesignShots().filter((shot) => shot.rowId === rowId);
    }
    /* ---------------------------------------------------------------- */
    /* Image-job state (`design.imgJob`)                                 */
    /* ---------------------------------------------------------------- */
    /**
     * Publish (or clear) the live image-job state. SERVER-ONLY, under a DEDICATED
     * machine origin — never a user origin, or a progress tick would land in that
     * user's undo stack (C6/C8). Exactly the discipline `setTranslationJob`
     * documents; see the schema note on `DESIGN_JOB_KEY`.
     */
    setDesignJob(state, origin) {
        const design = this.designMap();
        if (state === undefined) {
            if (!design.has(DESIGN_JOB_KEY))
                return; // idempotent clear
            this.transact(() => {
                design.delete(DESIGN_JOB_KEY);
            }, origin);
            return;
        }
        // Normalized COPY: keeping the caller's reference would let the job loop's
        // later mutation fork doc state without a transaction, and never broadcast.
        const stored = {
            jobId: state.jobId,
            runId: state.runId,
            status: state.status,
            done: state.done,
            total: state.total,
            failed: state.failed,
            startedAt: state.startedAt,
        };
        if (state.message !== undefined)
            stored.message = state.message;
        this.transact(() => {
            design.set(DESIGN_JOB_KEY, stored);
        }, origin);
    }
    /** The board's live image-job state, or undefined. Fresh copy. */
    getDesignJob() {
        const raw = this.designMap().get(DESIGN_JOB_KEY);
        if (!isDesignJobState(raw))
            return undefined;
        const out = {
            jobId: raw.jobId,
            runId: raw.runId,
            status: raw.status,
            done: raw.done,
            total: raw.total,
            failed: raw.failed,
            startedAt: raw.startedAt,
        };
        if (raw.message !== undefined)
            out.message = raw.message;
        return out;
    }
    /* ---------------------------------------------------------------- */
    /* Thumbnails (`thumbs` root map) — F-130                             */
    /* ---------------------------------------------------------------- */
    /**
     * The words on the thumbnails. Two independent LWW keys (C2): one person can
     * sharpen the title while another rewrites the hook text.
     *
     * Empty = "let the generator write it": the run writes its own hooks from
     * the script when these are blank, and never writes INTO these keys — they
     * stay the user's, exactly like the design brief.
     */
    setThumbTitle(title, origin) {
        const thumbs = this.thumbsMap();
        this.transact(() => {
            thumbs.set(THUMB_TITLE_KEY, title.slice(0, THUMB_TITLE_MAX_LENGTH));
        }, origin);
    }
    getThumbTitle() {
        const raw = this.thumbsMap().get(THUMB_TITLE_KEY);
        return typeof raw === "string" ? raw : "";
    }
    setThumbText(text, origin) {
        const thumbs = this.thumbsMap();
        this.transact(() => {
            thumbs.set(THUMB_TEXT_KEY, text.slice(0, THUMB_TEXT_MAX_LENGTH));
        }, origin);
    }
    getThumbText() {
        const raw = this.thumbsMap().get(THUMB_TEXT_KEY);
        return typeof raw === "string" ? raw : "";
    }
    /**
     * The model picks — board content, not a per-reader preference (see the
     * schema note). `undefined` clears the key, which reads as "the server's
     * default" everywhere.
     */
    setThumbImageModel(model, origin) {
        this.setThumbSetting(THUMB_IMAGE_MODEL_KEY, model, origin);
    }
    getThumbImageModel() {
        return this.thumbSetting(THUMB_IMAGE_MODEL_KEY);
    }
    setThumbTextModel(model, origin) {
        this.setThumbSetting(THUMB_TEXT_MODEL_KEY, model, origin);
    }
    getThumbTextModel() {
        return this.thumbSetting(THUMB_TEXT_MODEL_KEY);
    }
    /** The shot the team picked, or `undefined` to clear the pick. */
    setSelectedThumb(shotId, origin) {
        this.setThumbSetting(THUMB_SELECTED_KEY, shotId, origin);
    }
    getSelectedThumb() {
        return this.thumbSetting(THUMB_SELECTED_KEY);
    }
    /**
     * Publish (or clear) the live run state. SERVER-ONLY, under a machine origin
     * — the same discipline as `setDesignJob`, for the same C6/C8 reason.
     */
    setThumbJob(state, origin) {
        const thumbs = this.thumbsMap();
        if (state === undefined) {
            if (!thumbs.has(THUMB_JOB_KEY))
                return; // idempotent clear
            this.transact(() => {
                thumbs.delete(THUMB_JOB_KEY);
            }, origin);
            return;
        }
        const stored = {
            jobId: state.jobId,
            runId: state.runId,
            status: state.status,
            done: state.done,
            total: state.total,
            failed: state.failed,
            startedAt: state.startedAt,
        };
        if (state.phase !== undefined)
            stored.phase = state.phase;
        if (state.message !== undefined)
            stored.message = state.message;
        this.transact(() => {
            thumbs.set(THUMB_JOB_KEY, stored);
        }, origin);
    }
    /** The board's live thumbnail-run state, or undefined. Fresh copy. */
    getThumbJob() {
        const raw = this.thumbsMap().get(THUMB_JOB_KEY);
        if (!isThumbJobState(raw))
            return undefined;
        const out = {
            jobId: raw.jobId,
            runId: raw.runId,
            status: raw.status,
            done: raw.done,
            total: raw.total,
            failed: raw.failed,
            startedAt: raw.startedAt,
        };
        if (raw.phase !== undefined)
            out.phase = raw.phase;
        if (raw.message !== undefined)
            out.message = raw.message;
        return out;
    }
    /* ---------------------------------------------------------------- */
    /* Thumbnail portraits (`thumbs/portraits`)                          */
    /* ---------------------------------------------------------------- */
    /**
     * Add a portrait at the END of the list. The first portrait in order is
     * the presenter; `moveThumbPortraitToFront` promotes another one. Nested
     * per-entity map keyed by a client-generated id, like the design refs.
     */
    addThumbPortrait(portraitId, init, origin) {
        this.transact(() => {
            const portraits = getThumbPortraits(this.doc);
            if (portraits.has(portraitId))
                return; // idempotent
            const ord = planInsertAtIndex(ordEntriesOf(portraits), portraits.size).ord;
            const entry = new Y.Map();
            entry.set("assetId", init.assetId); // immutable after creation
            entry.set("w", init.w);
            entry.set("h", init.h);
            entry.set("ord", ord);
            entry.set("label", (init.label ?? "").slice(0, THUMB_PORTRAIT_LABEL_MAX_LENGTH));
            portraits.set(portraitId, entry);
        }, origin);
    }
    /** Who this portrait is — its own key, independently editable (C2). */
    setThumbPortraitLabel(portraitId, label, origin) {
        this.transact(() => {
            getThumbPortraits(this.doc)
                .get(portraitId)
                ?.set("label", label.slice(0, THUMB_PORTRAIT_LABEL_MAX_LENGTH));
        }, origin);
    }
    /**
     * Make this portrait the presenter: one `ord` rewrite that sorts it before
     * every other entry (C1), so two people promoting two different portraits
     * concurrently converge on a total order rather than a tie.
     */
    moveThumbPortraitToFront(portraitId, origin) {
        this.transact(() => {
            const portraits = getThumbPortraits(this.doc);
            const entry = portraits.get(portraitId);
            if (entry === undefined)
                return;
            const others = sortOrdered(ordEntriesOf(portraits).filter((e) => e.id !== portraitId));
            const plan = planInsertAtIndex(others, 0);
            for (const rewrite of plan.rewrites) {
                portraits.get(rewrite.id)?.set("ord", rewrite.ord);
            }
            entry.set("ord", plan.ord);
        }, origin);
    }
    deleteThumbPortrait(portraitId, origin) {
        this.transact(() => {
            getThumbPortraits(this.doc).delete(portraitId);
        }, origin);
    }
    /** Every portrait, presenter first, in `(ord, id)` order. */
    listThumbPortraits() {
        const portraits = getThumbPortraits(this.doc);
        const out = [];
        for (const { id } of sortOrdered(ordEntriesOf(portraits))) {
            const entry = portraits.get(id);
            if (entry === undefined)
                continue;
            const assetId = entry.get("assetId");
            if (typeof assetId !== "string")
                continue; // half-written / foreign entry
            out.push({
                id,
                assetId,
                w: numberOr(entry.get("w"), 0),
                h: numberOr(entry.get("h"), 0),
                ord: typeof entry.get("ord") === "string" ? entry.get("ord") : "",
                label: typeof entry.get("label") === "string" ? entry.get("label") : "",
            });
        }
        return out;
    }
    /* ---------------------------------------------------------------- */
    /* Generated thumbnails (`thumbs/shots`)                             */
    /* ---------------------------------------------------------------- */
    /**
     * Record one generated thumbnail. SERVER-side, under a machine origin — the
     * run writes these as they land, and the refine route writes one at a time.
     * Never replaces anything: runs and refinements accumulate.
     */
    addThumbShot(shotId, init, origin) {
        this.transact(() => {
            const shots = getThumbShots(this.doc);
            if (shots.has(shotId))
                return; // idempotent
            const ord = init.ord ?? planInsertAtIndex(ordEntriesOf(shots), shots.size).ord;
            const entry = new Y.Map();
            entry.set("assetId", init.assetId);
            entry.set("w", init.w);
            entry.set("h", init.h);
            entry.set("runId", init.runId);
            entry.set("ord", ord);
            entry.set("createdAt", init.createdAt);
            entry.set("prompt", init.prompt);
            entry.set("model", init.model);
            entry.set("concept", init.concept);
            entry.set("text", init.text);
            entry.set("title", init.title);
            if (init.parentId !== undefined)
                entry.set("parentId", init.parentId);
            if (init.feedback !== undefined)
                entry.set("feedback", init.feedback);
            if (init.portraitId !== undefined)
                entry.set("portraitId", init.portraitId);
            if (init.elementId !== undefined)
                entry.set("elementId", init.elementId);
            shots.set(shotId, entry);
        }, origin);
    }
    /** Point a shot at the board element showing it (or clear it). Own key. */
    setThumbShotElement(shotId, elementId, origin) {
        this.transact(() => {
            const entry = getThumbShots(this.doc).get(shotId);
            if (entry === undefined)
                return;
            if (elementId === undefined)
                entry.delete("elementId");
            else
                entry.set("elementId", elementId);
        }, origin);
    }
    deleteThumbShot(shotId, origin) {
        this.transact(() => {
            getThumbShots(this.doc).delete(shotId);
        }, origin);
    }
    /** A single shot by id, or undefined. */
    getThumbShot(shotId) {
        return this.listThumbShots().find((shot) => shot.id === shotId);
    }
    /** Every generated thumbnail, in `(ord, id)` order — creation order. */
    listThumbShots() {
        const shots = getThumbShots(this.doc);
        const out = [];
        for (const { id } of sortOrdered(ordEntriesOf(shots))) {
            const entry = shots.get(id);
            if (entry === undefined)
                continue;
            const assetId = entry.get("assetId");
            const runId = entry.get("runId");
            if (typeof assetId !== "string" || typeof runId !== "string")
                continue;
            const shot = {
                id,
                assetId,
                runId,
                w: numberOr(entry.get("w"), 0),
                h: numberOr(entry.get("h"), 0),
                ord: typeof entry.get("ord") === "string" ? entry.get("ord") : "",
                createdAt: numberOr(entry.get("createdAt"), 0),
                prompt: typeof entry.get("prompt") === "string" ? entry.get("prompt") : "",
                model: typeof entry.get("model") === "string" ? entry.get("model") : "",
                concept: numberOr(entry.get("concept"), 0),
                text: typeof entry.get("text") === "string" ? entry.get("text") : "",
                title: typeof entry.get("title") === "string" ? entry.get("title") : "",
            };
            const parentId = entry.get("parentId");
            if (typeof parentId === "string")
                shot.parentId = parentId;
            const feedback = entry.get("feedback");
            if (typeof feedback === "string")
                shot.feedback = feedback;
            const portraitId = entry.get("portraitId");
            if (typeof portraitId === "string")
                shot.portraitId = portraitId;
            const elementId = entry.get("elementId");
            if (typeof elementId === "string")
                shot.elementId = elementId;
            out.push(shot);
        }
        return out;
    }
    /* ---------------------------------------------------------------- */
    /* Permanent reorder (C3)                                            */
    /* ---------------------------------------------------------------- */
    /**
     * The explicit "Reorder rows permanently" command: sorts the current rows
     * with `comparator` (stable — ties keep the current `(ord, id)` order) and
     * rewrites every row's `ord` key with fresh balanced keys in ONE
     * transaction (one sync unit, one undo step).
     *
     * Merge safety (C1): only `ord` values inside existing rowMeta entries are
     * written. A concurrently-deleted row's entry deletion wins over the ord
     * write (no resurrection); a concurrently-inserted row keeps its own key
     * and lands somewhere in the new order.
     *
     * ROWS ONLY — it never touches colMeta, so the F-28 source column cannot be
     * displaced by a reorder (asserted in source-column.test).
     */
    permanentReorder(sheetId, comparator, origin) {
        this.transact(() => {
            const rowMeta = getRowMeta(this.doc, sheetId);
            const idsInOrder = this.sortedRowIds(sheetId).sort(comparator);
            for (const { id, ord } of rebalance(idsInOrder)) {
                rowMeta.get(id)?.set("ord", ord);
            }
        }, origin);
    }
    /* ---------------------------------------------------------------- */
    /* Cells (C2: v / st / one key per format prop — FLAT keys; see       */
    /* schema.ts `cellKey` for why not a lazily-created nested map)       */
    /* ---------------------------------------------------------------- */
    getCell(sheetId, rowId, colId) {
        const cells = getCells(this.doc, sheetId);
        const rawV = cells.get(cellPropKey(rowId, colId, "v"));
        const st = cells.get(cellPropKey(rowId, colId, "st"));
        const ty = this.getCellTextTypes(sheetId, rowId, colId);
        const fmt = this.readCellFormat(sheetId, rowId, colId);
        if (rawV === undefined && fmt === undefined && st === undefined && ty.length === 0) {
            return undefined;
        }
        const v = rawV instanceof Y.Text ? rawV.toString() : (rawV ?? null);
        const snapshot = { v };
        if (fmt?.bg !== undefined)
            snapshot.bg = fmt.bg;
        if (fmt !== undefined)
            snapshot.fmt = fmt;
        if (st !== undefined)
            snapshot.st = st;
        if (ty.length > 0)
            snapshot.ty = ty;
        // F-17-marks: only for a cell that HAS text — a mark resolves against the
        // text it marks, so on an empty or non-text cell there is nothing to
        // resolve and four map reads per render would buy nothing.
        if (typeof v === "string" && v.length > 0) {
            const marks = this.readCellMarks(sheetId, rowId, colId);
            if (marks !== undefined)
                snapshot.marks = marks;
        }
        return snapshot;
    }
    /**
     * F-17-marks: the cell's marks of ONE kind, resolved-ready but NOT yet
     * resolved (offsets are still the stored hints — `resolveTextMarks` in
     * ../text-marks.ts turns them into spans against the current text).
     */
    getCellMarks(sheetId, rowId, colId, kind) {
        return sanitizeTextMarks(getCells(this.doc, sheetId).get(cellPropKey(rowId, colId, cellMarkProp(kind))));
    }
    /**
     * Write ONE mark kind's list — its own flat CRDT key (C2), so bolding a
     * phrase merges with a peer highlighting another, with a concurrent fill,
     * and with typing elsewhere in the same cell.
     *
     * An empty list DELETES the key rather than storing `[]`: unlike the format
     * props there is no retired whole-object entry for an empty value to have to
     * mask (schema.ts `legacyCellFmtKey` predates marks), so "no marks" is
     * exactly "no key" and a cleared cell costs nothing in the doc.
     */
    setCellMarks(sheetId, rowId, colId, kind, marks, origin) {
        const cells = getCells(this.doc, sheetId);
        const key = cellPropKey(rowId, colId, cellMarkProp(kind));
        if (marks.length === 0 && !cells.has(key))
            return;
        this.transact(() => {
            if (marks.length === 0)
                cells.delete(key);
            else
                cells.set(key, marks.map(cloneMark));
        }, origin);
    }
    /** Every mark kind this cell carries; undefined when it carries none. */
    readCellMarks(sheetId, rowId, colId) {
        const cells = getCells(this.doc, sheetId);
        let out;
        for (const kind of TEXT_MARK_KINDS) {
            const raw = cells.get(cellPropKey(rowId, colId, cellMarkProp(kind)));
            if (raw === undefined)
                continue;
            const marks = sanitizeTextMarks(raw);
            if (marks.length === 0)
                continue;
            out ??= {};
            out[kind] = marks;
        }
        return out;
    }
    /**
     * F-17-marks + F-17-type: carry this cell's marks AND its line-kind
     * switches across a text change made by THIS writer, inside the same
     * transaction as the value write.
     *
     * Both re-anchor by their quoted text on read, which covers a peer's
     * concurrent edit — but only approximately, and not at all for an edit made
     * INSIDE a marked range. Rebasing the writer's own edit (../text-marks.ts
     * `rebaseTextMarks`, ../text-types.ts `rebaseTextTypeSwitches`) is what
     * keeps typing exact: insert a word before a bold phrase and the phrase
     * stays bold, delete half of it and the mark shrinks to what is left, type
     * at a switch the `/` menu just placed and the words land in its kind.
     */
    rebaseCellMarks(sheetId, rowId, colId, before, after, origin) {
        if (before === after)
            return;
        const cells = getCells(this.doc, sheetId);
        for (const kind of TEXT_MARK_KINDS) {
            const key = cellPropKey(rowId, colId, cellMarkProp(kind));
            const raw = cells.get(key);
            if (raw === undefined)
                continue;
            const stored = sanitizeTextMarks(raw);
            if (stored.length === 0)
                continue;
            this.setCellMarks(sheetId, rowId, colId, kind, rebaseTextMarks(before, after, stored), origin);
        }
        const types = this.getCellTextTypes(sheetId, rowId, colId);
        if (types.length > 0) {
            this.setCellTextTypes(sheetId, rowId, colId, rebaseTextTypeSwitches(before, after, types), origin);
        }
    }
    /**
     * Cell content as text ("" when absent/empty). Used for content hashing.
     * Image content (F-30) has no text — it hashes as empty, and C5.1's
     * occupancy check (which sees the image object via `getCell`) is what keeps
     * machine writers out of image cells.
     */
    getCellText(sheetId, rowId, colId) {
        const raw = getCells(this.doc, sheetId).get(cellPropKey(rowId, colId, "v"));
        if (raw === undefined || raw === null)
            return "";
        if (raw instanceof Y.Text)
            return raw.toString();
        if (isImageCellValue(raw))
            return "";
        // F-126: a BGM slot DOES have text — its stems' titles. Unlike an image
        // it is written as prose the reader is meant to read (and CSV export
        // carries), so it hashes and renders as those titles rather than as empty.
        if (isBgmCellValue(raw))
            return bgmCellText(raw);
        return String(raw);
    }
    /**
     * Write ONLY the `v` key (C2 — never touches format props or st).
     * `undefined` DELETES the key (vacates the cell — used by the legacy
     * image-cell migration); `null` writes an explicit empty value.
     *
     * C7 refinement (docs/04 §2, docs/07 C7): columns typed `longText` store
     * `v` as a `Y.Text` for character-level merge of simultaneous typing
     * (translation source/target columns default to long text):
     *  - an existing Y.Text is NEVER replaced wholesale — the new string is
     *    spliced in as a prefix/suffix diff, so concurrent character edits from
     *    other replicas survive the commit;
     *  - the first string write to a longText cell creates the Y.Text.
     * Other column types keep plain scalar LWW (C7 accepted behavior).
     *
     * Image content (F-30) is a whole-object value under `v`: it always
     * replaces what was there — including a longText Y.Text, because switching
     * a cell from text to image is a deliberate whole-value type change, not a
     * text edit to be merged.
     */
    setCellValue(sheetId, rowId, colId, value, origin) {
        this.transact(() => {
            const cells = getCells(this.doc, sheetId);
            const key = cellPropKey(rowId, colId, "v");
            // F-17-marks and F-17-type switches ride along on every value write, in
            // the SAME transaction: one sync unit, one undo step, and never a frame
            // where the text and its marks disagree about where the bold phrase is,
            // or where the sound cue starts.
            //
            // Gated on the cell HAVING any, and the new text read lazily, because
            // this is the hot path: the F-90 bulk import writes a value per row, and
            // reading the before/after text of a long-text cell is O(text) each way.
            // Almost every cell on a board carries neither, and for those this costs
            // five map lookups and nothing else.
            const marked = this.cellHasMarks(sheetId, rowId, colId);
            const before = marked ? this.getCellText(sheetId, rowId, colId) : "";
            const rebase = (after) => {
                if (marked)
                    this.rebaseCellMarks(sheetId, rowId, colId, before, after(), origin);
            };
            if (value === undefined) {
                if (cells.has(key))
                    cells.delete(key);
                if (marked)
                    this.clearCellMarks(sheetId, rowId, colId, origin);
                return;
            }
            if (isImageCellValue(value)) {
                cells.set(key, value);
                // An image is not text: nothing left for a text range to mark.
                if (marked)
                    this.clearCellMarks(sheetId, rowId, colId, origin);
                return;
            }
            // F-126: a BGM stem is a whole-object value like an image — it replaces
            // whatever was there (a longText Y.Text included; switching a cell from
            // prose to a stem is a type change, not a text edit to merge). Its title
            // IS readable text, but it is a LABEL the stem owns, not a passage a
            // reviewer marked up, so any surviving marks are dropped rather than
            // rebased onto a string the user never typed.
            if (isBgmCellValue(value)) {
                cells.set(key, value);
                if (marked)
                    this.clearCellMarks(sheetId, rowId, colId, origin);
                return;
            }
            const existing = cells.get(key);
            if (existing instanceof Y.Text) {
                // Keep the shared type alive: splice the diff instead of replacing.
                spliceYText(existing, value === null ? "" : String(value));
                rebase(() => existing.toString());
                return;
            }
            if (typeof value === "string" && this.getColumn(sheetId, colId)?.type === "longText") {
                const text = new Y.Text();
                text.insert(0, value);
                cells.set(key, text);
                rebase(() => value);
                return;
            }
            cells.set(key, value);
            rebase(() => (value === null ? "" : String(value)));
        }, origin);
    }
    /**
     * Does this cell carry any mark, or any line-kind switch? The value path's
     * cheap gate.
     */
    cellHasMarks(sheetId, rowId, colId) {
        const cells = getCells(this.doc, sheetId);
        for (const kind of TEXT_MARK_KINDS) {
            if (cells.has(cellPropKey(rowId, colId, cellMarkProp(kind))))
                return true;
        }
        return cells.has(cellPropKey(rowId, colId, "ty"));
    }
    /**
     * Drop every mark kind and every line-kind switch of a cell (its text is
     * gone or is no longer text — an image, a BGM stem).
     */
    clearCellMarks(sheetId, rowId, colId, origin) {
        for (const kind of TEXT_MARK_KINDS) {
            this.setCellMarks(sheetId, rowId, colId, kind, [], origin);
        }
        this.setCellTextTypes(sheetId, rowId, colId, [], origin);
    }
    /**
     * A HUMAN edit to a cell: writes `v` AND clears `st` in one transaction —
     * the F-70.5 rule ("any human edit to a target clears st") enforced here in
     * the shared accessor layer so EVERY human write path (single cell edit,
     * multi-cell paste, future bulk edits) gets it for free instead of each UI
     * path re-implementing it. Machine writers (translation worker, AI actions)
     * must use `guardedCellWrite` instead, which stamps `st` deliberately.
     */
    setCellValueAsHuman(sheetId, rowId, colId, value, origin) {
        this.transact(() => {
            this.setCellValue(sheetId, rowId, colId, value, origin);
            this.setCellStatus(sheetId, rowId, colId, undefined, origin);
        }, origin);
    }
    /**
     * Human drop/paste of an image into a cell (F-30: pointer over a grid cell
     * at drop time). Convenience over `setCellValueAsHuman`: the image object
     * goes under the cell's `v` key ONLY (C2 — format props untouched), and `st`
     * is cleared with human-edit semantics (F-70.5) since a person placed it.
     * The stored value is normalized to exactly `{ kind, assetId, w, h }`.
     *
     * NOTE (2026-07-14): images are ALWAYS elements now (F-11 image cell type
     * retired). This stays for the migration's round-trip and legacy docs — see
     * `migrateImageCellsToElements`.
     */
    setCellImage(sheetId, rowId, colId, image, origin) {
        const value = {
            kind: "image",
            assetId: image.assetId,
            w: image.w,
            h: image.h,
        };
        this.setCellValueAsHuman(sheetId, rowId, colId, value, origin);
    }
    /**
     * F-126: write a BGM slot into `colId`'s cell on `rowId` — the top cell of
     * one mood run in the board's "BGM" column — REPLACING whatever was there.
     * `alternates` (F-139) ride along when given; `addCellBgmStem` is the
     * usual way in, this is the whole-slot write behind it.
     *
     * Every stem is normalized to exactly the fields its `source` uses, so a
     * stem switched from an uploaded file to a pasted link cannot keep a stale
     * `assetId` alongside its new `url` (the guard would still pass, and the
     * orphan sweep would never learn the asset was dropped).
     *
     * Written through `setCellValueAsHuman` like every other deliberate human
     * edit: it clears any `st` translation marker on the cell, and it is one
     * `v` write, so it merges under C2 exactly as an image does.
     */
    setCellBgm(sheetId, rowId, colId, stem, origin) {
        const value = { kind: "bgm", ...normalizeBgmStem(stem) };
        if (stem.alternates !== undefined && stem.alternates.length > 0) {
            value.alternates = stem.alternates.map(normalizeBgmStem);
        }
        this.setCellValueAsHuman(sheetId, rowId, colId, value, origin);
    }
    /**
     * F-139: ADD a stem to the slot on `rowId` — the first stem when the cell
     * is empty, an alternate after that. The slot's list is ONE value under `v`
     * (C2: "the candidates for this run" is one semantic unit), so two people
     * adding at the same moment is a last-writer-wins on the value — both see
     * the winner's list, and the other stem is one re-add away, which beats
     * a list that can hold a half-written entry.
     */
    addCellBgmStem(sheetId, rowId, colId, stem, origin) {
        const existing = this.getCellBgm(sheetId, rowId, colId);
        if (existing === undefined) {
            this.setCellBgm(sheetId, rowId, colId, stem, origin);
            return;
        }
        const stems = [...bgmStemsOf(existing), stem];
        this.setCellBgm(sheetId, rowId, colId, { ...stems[0], alternates: stems.slice(1) }, origin);
    }
    /**
     * F-139: remove the stem at `index` (in `bgmStemsOf` order) from the slot
     * on `rowId`. Removing the last one EMPTIES the cell (`null` — the slot
     * stays, the music goes). An index that is not there is a no-op.
     */
    removeCellBgmStem(sheetId, rowId, colId, index, origin) {
        const existing = this.getCellBgm(sheetId, rowId, colId);
        if (existing === undefined)
            return;
        const all = bgmStemsOf(existing);
        if (index < 0 || index >= all.length)
            return;
        const stems = all.filter((_, i) => i !== index);
        if (stems.length === 0) {
            this.setCellValueAsHuman(sheetId, rowId, colId, null, origin);
            return;
        }
        this.setCellBgm(sheetId, rowId, colId, { ...stems[0], alternates: stems.slice(1) }, origin);
    }
    /**
     * The BGM stem stored on a cell, or undefined when there is none. A typed
     * read so callers never re-implement the `isBgmCellValue` narrowing.
     */
    getCellBgm(sheetId, rowId, colId) {
        const raw = getCells(this.doc, sheetId).get(cellPropKey(rowId, colId, "v"));
        return isBgmCellValue(raw) ? raw : undefined;
    }
    /**
     * Every inline image cell still stored on `sheetId`, as `{rowId, colId,
     * image}`. Drives the one-shot legacy migration to contained elements
     * (`migrateImageCellsToElements`) and its per-sheet "is there anything to do"
     * guard, so a modern board never opens a transaction on load.
     */
    listImageCells(sheetId) {
        const out = [];
        getCells(this.doc, sheetId).forEach((value, key) => {
            const parsed = parseCellPropKey(key);
            if (parsed === null || parsed.prop !== "v")
                return;
            if (!isImageCellValue(value))
                return;
            out.push({ rowId: parsed.rowId, colId: parsed.colId, image: value });
        });
        return out;
    }
    /**
     * F-17-bg: the cell's background fill — its OWN flat CRDT key (C2), so a
     * fill merges with concurrent typing, a machine translation, or any other
     * format property. `undefined` clears it.
     *
     * Clearing writes an explicit `null` rather than deleting the key, but ONLY
     * when there is something to mask (an existing bg, or a legacy whole-object
     * `fmt` entry that a plain delete would let resurface — schema.ts
     * `legacyCellFmtKey`). On a cell with neither, "clear" is already true and
     * writes nothing.
     */
    setCellBg(sheetId, rowId, colId, bg, origin) {
        this.setCellFormatProp(sheetId, rowId, colId, "bg", bg, origin);
    }
    /** F-17-bg read: the per-property key, falling back to the legacy shim. */
    getCellBg(sheetId, rowId, colId) {
        return this.readCellFormatProp(sheetId, rowId, colId, "bg");
    }
    /**
     * F-17-color: the cell's TEXT color — its own flat CRDT key (C2), exactly
     * like `bg`, so recoloring text merges with a concurrent fill, typing, or
     * translation instead of clobbering it. `undefined` clears it, which is not
     * "black" but "no explicit color": the client then derives readable ink from
     * the cell's fill and the current theme (client core/color.ts).
     *
     * Also drops any per-run "color" marks (F-17-marks) the cell carries: a
     * whole-cell color pick is the user saying "the whole text is this color
     * now," and a leftover mark from an earlier partial-selection recolor would
     * otherwise keep winning over it in `MarkedText`'s span-level style.
     */
    setCellColor(sheetId, rowId, colId, color, origin) {
        this.transact(() => {
            this.setCellFormatProp(sheetId, rowId, colId, "color", color, origin);
            this.setCellMarks(sheetId, rowId, colId, "color", [], origin);
        }, origin);
    }
    /** F-17-color read: the per-property key, falling back to the legacy shim. */
    getCellColor(sheetId, rowId, colId) {
        return this.readCellFormatProp(sheetId, rowId, colId, "color");
    }
    /**
     * DEPRECATED whole-object alias, kept for call sites that carry a derived
     * `CellFormat` around (clipboard/duplicate paths). It fans the object out
     * across the per-property keys — the retired `"rowId:colId:fmt"` key is
     * NEVER written (schema.ts, 2026-07-13). Whole-replace semantics: a property
     * absent from `fmt` is CLEARED, so this can revert a concurrent writer of
     * that property. Prefer `setCellBg` (and future per-property setters) on any
     * path where two users can act at once.
     */
    setCellFormat(sheetId, rowId, colId, fmt, origin) {
        this.transact(() => {
            for (const prop of CELL_FORMAT_PROPS) {
                this.setCellFormatProp(sheetId, rowId, colId, prop, fmt?.[prop], origin);
            }
        }, origin);
    }
    /**
     * Write ONLY the `st` key; `undefined` clears it (the "human edit clears
     * machine-translation status" rule — F-70.5).
     */
    setCellStatus(sheetId, rowId, colId, st, origin) {
        this.transact(() => {
            const cells = getCells(this.doc, sheetId);
            const key = cellPropKey(rowId, colId, "st");
            if (st === undefined) {
                if (cells.has(key))
                    cells.delete(key);
            }
            else {
                cells.set(key, st);
            }
        }, origin);
    }
    /**
     * F-17-type: the cell's line-kind switch points in STORED form (offsets
     * are hints; `textTypeRuns` / `resolveTextTypeSwitches` in ../text-types.ts
     * place them against the current text). Empty for an all-narration cell.
     *
     * A read of its own, rather than `getCell(...)?.ty`, because the script
     * view asks this once per block on every cells tick and a whole snapshot
     * (marks resolution included) is far more work than one map get. The
     * retired whole-cell scalar reads as one switch at offset 0
     * (`sanitizeTextTypeSwitches`).
     */
    getCellTextTypes(sheetId, rowId, colId) {
        return sanitizeTextTypeSwitches(getCells(this.doc, sheetId).get(cellPropKey(rowId, colId, "ty")));
    }
    /**
     * Write ONLY the `ty` key. An empty list DELETES the key rather than
     * storing `[]`: narration is the absence of the key, which keeps every cell
     * written before this feature indistinguishable from one a writer set back
     * to narration, as it should be. Always the list form — the whole-cell
     * scalar is read-compat only.
     */
    setCellTextTypes(sheetId, rowId, colId, switches, origin) {
        const cells = getCells(this.doc, sheetId);
        const key = cellPropKey(rowId, colId, "ty");
        if (switches.length === 0 && !cells.has(key))
            return;
        this.transact(() => {
            if (switches.length === 0)
                cells.delete(key);
            else
                cells.set(key, switches.map(cloneSwitch));
        }, origin);
    }
    /* ---------------------------------------------------------------- */
    /* Elements (C2: per-property keys)                                  */
    /* ---------------------------------------------------------------- */
    addElement(elementId, init, origin) {
        this.transact(() => {
            const elements = getElements(this.doc);
            if (elements.has(elementId))
                return;
            const entry = new Y.Map();
            entry.set("type", init.type); // immutable after creation
            entry.set("x", init.x);
            entry.set("y", init.y);
            entry.set("w", init.w);
            entry.set("h", init.h);
            entry.set("z", init.z);
            entry.set("rotation", init.rotation ?? 0);
            entry.set("anchor", init.anchor);
            entry.set("payload", init.payload);
            if (init.by !== undefined)
                entry.set("by", init.by); // immutable after creation
            elements.set(elementId, entry);
        }, origin);
    }
    /**
     * Create an image element (F-30 — images are ALWAYS elements). `w`/`h` =
     * displayed size in board units (per-property keys — C2); `payload.w`/`h` =
     * the image's natural pixel size (immutable, for aspect-ratio-preserving
     * resize).
     *
     * `anchor` omitted (drop over empty canvas) → board-anchored at `(x, y)`.
     * Pass a `{kind:"cell",…}` anchor (drop over a grid cell) → a CONTAINED
     * element (F-36); the caller then ratchets the cell to fit it (F-26) in the
     * SAME transaction — see `ratchetCellToElements`.
     *
     * `z` = one above the current top so the new element lands frontmost. Two
     * clients creating concurrently can compute equal `z` values — rendering
     * breaks ties by `(z, id)` (`listElements`), so the stack stays total and
     * identical on all replicas.
     */
    createImageElement(elementId, x, y, w, h, payload, origin, anchor, by) {
        this.transact(() => {
            this.addElement(elementId, {
                type: "image",
                x,
                y,
                w,
                h,
                z: this.topZ() + 1,
                anchor: anchor ?? { kind: "board", x, y },
                payload: {
                    kind: "image",
                    assetId: payload.assetId,
                    w: payload.w,
                    h: payload.h,
                    // F-135: the original file name, only when the writer knew one —
                    // no `undefined`-valued key leaks into the doc.
                    ...(payload.name === undefined ? {} : { name: payload.name }),
                },
                ...(by === undefined ? {} : { by }),
            }, origin);
        }, origin);
    }
    /**
     * F-135: create a `file` element — any uploaded file that is not a board
     * image (a video, an audio clip, a PDF, …). `w`/`h` = displayed size in
     * board units (per-property keys — C2); the payload carries the server's
     * description of the bytes (sniffed type, size, how to present it) and a
     * video's intrinsic size when known, immutable for the asset's lifetime.
     *
     * `anchor` follows the same rule as `createImageElement` (F-30/F-36): omitted
     * → board-anchored at `(x, y)`; a cell anchor → a CONTAINED element whose
     * cell the caller ratchets in the same transaction. The stored payload is a
     * normalized copy carrying only the fields actually provided.
     */
    createFileElement(elementId, x, y, w, h, payload, origin, anchor, by) {
        this.transact(() => {
            const stored = {
                kind: "file",
                assetId: payload.assetId,
                name: payload.name,
                contentType: payload.contentType,
                size: payload.size,
                media: payload.media,
            };
            if (payload.w !== undefined && payload.h !== undefined) {
                stored.w = payload.w;
                stored.h = payload.h;
            }
            this.addElement(elementId, {
                type: "file",
                x,
                y,
                w,
                h,
                z: this.topZ() + 1,
                anchor: anchor ?? { kind: "board", x, y },
                payload: stored,
                ...(by === undefined ? {} : { by }),
            }, origin);
        }, origin);
    }
    /**
     * F-31: create a link-card element. Callers pass the SKELETON payload
     * (`{url, domain?, status:"loading"}`) so the card appears instantly; the
     * server's SSRF-guarded unfurl later patches the whole `payload` key with
     * title/description/thumbnail (or `status:"error"`).
     *
     * `anchor` follows the same rule as `createImageElement` (F-30/F-36): omitted
     * → board-anchored at `(x, y)`; a cell anchor → contained. The stored payload
     * is a normalized copy carrying only the fields actually provided — no
     * `undefined`-valued keys leak into the doc.
     */
    createLinkCardElement(elementId, x, y, w, h, payload, origin, anchor, by) {
        this.transact(() => {
            this.addElement(elementId, {
                type: "link-card",
                x,
                y,
                w,
                h,
                z: this.topZ() + 1,
                anchor: anchor ?? { kind: "board", x, y },
                payload: normalizeLinkCardPayload(payload),
                ...(by === undefined ? {} : { by }),
            }, origin);
        }, origin);
    }
    /**
     * Create a `drawing` (freehand ink) element. `w`/`h` = the stroke's bounding
     * box in board units (its own per-property keys — C2); the vertices in
     * `payload.d` are element-local (`[0..w] × [0..h]`), so a later resize scales
     * the drawing via the renderer's viewBox rather than rewriting the payload.
     *
     * `anchor` follows the same rule as `createImageElement`/`createLinkCardElement`
     * (F-30/F-36): omitted (stroke started over empty canvas) → board-anchored at
     * `(x, y)`; a `{kind:"cell",…}` anchor (stroke started over a grid cell) → the
     * drawing FOLLOWS that cell. Unlike a contained image, a drawing does NOT grow
     * or confine to the cell (see the client's `contained.ts`), so the caller does
     * NOT ratchet — a stroke may extend past the cell's edges and stay attached.
     * The stored payload is a normalized copy (the `d` array is cloned so later
     * local mutation cannot fork doc state).
     */
    createDrawingElement(elementId, x, y, w, h, payload, origin, anchor, by) {
        this.transact(() => {
            this.addElement(elementId, {
                type: "drawing",
                x,
                y,
                w,
                h,
                z: this.topZ() + 1,
                anchor: anchor ?? { kind: "board", x, y },
                payload: {
                    kind: "drawing",
                    d: [...payload.d],
                    color: payload.color,
                    thickness: payload.thickness,
                },
                ...(by === undefined ? {} : { by }),
            }, origin);
        }, origin);
    }
    hasElement(elementId) {
        return getElements(this.doc).has(elementId);
    }
    getElement(elementId) {
        const entry = getElements(this.doc).get(elementId);
        if (!entry)
            return undefined;
        const by = entry.get("by");
        const info = {
            id: elementId,
            type: entry.get("type"),
            x: entry.get("x") ?? 0,
            y: entry.get("y") ?? 0,
            w: entry.get("w") ?? 0,
            h: entry.get("h") ?? 0,
            z: entry.get("z") ?? 0,
            rotation: entry.get("rotation") ?? 0,
            anchor: entry.get("anchor"),
            payload: entry.get("payload"),
        };
        // Absent on server-created and pre-`by` elements (AddElementInit.by), so
        // the key is only present when someone is actually there to notify.
        if (typeof by === "string" && by !== "")
            info.by = by;
        return info;
    }
    /** Move = writes only `x`/`y` — merges with a concurrent resize (C2). */
    moveElement(elementId, x, y, origin) {
        this.transact(() => {
            const entry = getElements(this.doc).get(elementId);
            if (!entry)
                return;
            entry.set("x", x);
            entry.set("y", y);
        }, origin);
    }
    /**
     * Write ONLY the provided geometry keys, in one transaction (one sync
     * unit, one undo step). C2: `x`/`y`/`w`/`h` are independent LWW keys — an
     * interaction must never re-write a key it did not change, or its stale
     * value silently reverts a concurrent writer of that key (the exact
     * move-vs-resize lost update the per-property layout exists to prevent).
     * The drag/resize commit path passes just the changed subset.
     */
    setElementGeometry(elementId, geometry, origin) {
        this.transact(() => {
            const entry = getElements(this.doc).get(elementId);
            if (!entry)
                return;
            if (geometry.x !== undefined)
                entry.set("x", geometry.x);
            if (geometry.y !== undefined)
                entry.set("y", geometry.y);
            if (geometry.w !== undefined)
                entry.set("w", geometry.w);
            if (geometry.h !== undefined)
                entry.set("h", geometry.h);
        }, origin);
    }
    /** Resize = writes only `w`/`h` — merges with a concurrent move (C2). */
    resizeElement(elementId, w, h, origin) {
        this.transact(() => {
            const entry = getElements(this.doc).get(elementId);
            if (!entry)
                return;
            entry.set("w", w);
            entry.set("h", h);
        }, origin);
    }
    setElementZ(elementId, z, origin) {
        this.transact(() => {
            getElements(this.doc).get(elementId)?.set("z", z);
        }, origin);
    }
    setElementRotation(elementId, rotation, origin) {
        this.transact(() => {
            getElements(this.doc).get(elementId)?.set("rotation", rotation);
        }, origin);
    }
    /** `anchor` is a single semantic unit — replaced whole, by design. */
    setElementAnchor(elementId, anchor, origin) {
        this.transact(() => {
            getElements(this.doc).get(elementId)?.set("anchor", anchor);
        }, origin);
    }
    /** `payload` is a single semantic unit — replaced whole, by design. */
    setElementPayload(elementId, payload, origin) {
        this.transact(() => {
            getElements(this.doc).get(elementId)?.set("payload", payload);
        }, origin);
    }
    deleteElement(elementId, origin) {
        this.transact(() => {
            getElements(this.doc).delete(elementId);
        }, origin);
    }
    listElementIds() {
        const ids = [];
        getElements(this.doc).forEach((_entry, id) => ids.push(id));
        return ids;
    }
    /**
     * All elements in paint order — sorted by `(z, id)`. The id tie-break makes
     * the stacking total and replica-identical even when concurrent creations
     * or `setElementZ` races produce equal `z` values.
     */
    listElements() {
        const out = [];
        getElements(this.doc).forEach((_entry, id) => {
            const info = this.getElement(id);
            if (info)
                out.push(info);
        });
        out.sort((a, b) => (a.z === b.z ? (a.id < b.id ? -1 : a.id > b.id ? 1 : 0) : a.z - b.z));
        return out;
    }
    /**
     * The elements CONTAINED by one cell (F-36) — those anchored
     * `{kind:"cell",rowId,colId,…}` — in paint order. `sheetId` is contextual:
     * the elements root is board-global (there is exactly one grid per board —
     * F-21), and row/col ids are unique within it.
     */
    elementsForCell(sheetId, rowId, colId) {
        void sheetId;
        return this.listElements().filter((el) => el.anchor.kind === "cell" && el.anchor.rowId === rowId && el.anchor.colId === colId);
    }
    /**
     * F-26/F-36 EXPAND-ONLY RATCHET: grow the cell's stored row height / column
     * width so its contained elements are never clipped, and NEVER shrink them
     * back (docs/04 §5). Returns true iff something actually grew.
     *
     * The stored size behaves exactly like a manual F-14 resize — shared,
     * persisted, LWW-convergent. Concurrent ratchets on two replicas LWW to one
     * value; each client re-runs this on its next observe pass and whoever is
     * short bumps up, converging on the max needed. Used by the CREATE/unfurl
     * paths, where extent can only be added; a drag-DROP instead readjusts both
     * directions via `fitCellToElements` (2026-07-17), so "moved away / smaller"
     * shrinks the cell back there.
     *
     * No-op (false) when the row/column was deleted concurrently (C4). Callers
     * invoke it INSIDE the transaction that created/moved/resized the element, so
     * the size bump ships as one sync unit / one undo step with the mutation.
     */
    ratchetCellToElements(sheetId, rowId, colId, origin) {
        return this.transact(() => {
            const row = this.getRow(sheetId, rowId);
            const col = this.getColumn(sheetId, colId);
            if (row === undefined || col === undefined)
                return false; // C4
            const plan = planCellRatchet(cellElementExtent(this, sheetId, rowId, colId), row.height, col.width);
            if (plan.rowHeight === undefined && plan.colWidth === undefined)
                return false;
            if (plan.rowHeight !== undefined)
                this.setRowHeight(sheetId, rowId, plan.rowHeight, origin);
            if (plan.colWidth !== undefined)
                this.setColumnWidth(sheetId, colId, plan.colWidth, origin);
            return true;
        }, origin);
    }
    /**
     * F-32/F-36 DROP-TIME FIT (2026-07-17): READJUST the cell's stored row
     * height / column width to the extents of the elements its row/column now
     * contains — BOTH directions, unlike the expand-only ratchet above. The
     * destination of a drag-drop grows so the dropped element is never clipped;
     * the source shrinks back once the element left it (never below the
     * `DEFAULT_ROW_HEIGHT`/`DEFAULT_COLUMN_WIDTH` floor). Returns true iff a
     * size actually changed.
     *
     * The extents are ROW/COLUMN-WIDE (`rowElementHeight`/`colElementWidth`),
     * not per-cell: the stored height is a per-ROW key, so shrinking it must
     * account for elements contained by EVERY cell of the row (and likewise the
     * width for every cell of the column) — a per-cell extent could clip a
     * neighbour. Growth from a row/col-wide extent is identical to the per-cell
     * ratchet's fixpoint (the stored size already floors at the widest cell).
     *
     * Shrinking never clips text — the stored size is a floor under the grid's
     * derived text auto-fit (docs/04 §5). A manual F-14 floor on the affected
     * row/column IS superseded: the drop is the newer intent about that cell's
     * size (same LWW convergence story as the ratchet — the last drop wins, and
     * any client whose elements still need more re-fits on its next pass).
     *
     * Same C4/transaction contract as `ratchetCellToElements`: no-op (false)
     * when the row/column was deleted concurrently; callers invoke it INSIDE
     * the transaction that moved/resized the element so the readjustment ships
     * as one sync unit / one undo step with the mutation.
     */
    fitCellToElements(sheetId, rowId, colId, origin) {
        return this.transact(() => {
            const row = this.getRow(sheetId, rowId);
            const col = this.getColumn(sheetId, colId);
            if (row === undefined || col === undefined)
                return false; // C4
            const plan = planCellFit({ w: colElementWidth(this, sheetId, colId), h: rowElementHeight(this, sheetId, rowId) }, row.height, col.width, DEFAULT_ROW_HEIGHT, DEFAULT_COLUMN_WIDTH);
            if (plan.rowHeight === undefined && plan.colWidth === undefined)
                return false;
            if (plan.rowHeight !== undefined)
                this.setRowHeight(sheetId, rowId, plan.rowHeight, origin);
            if (plan.colWidth !== undefined)
                this.setColumnWidth(sheetId, colId, plan.colWidth, origin);
            return true;
        }, origin);
    }
    /**
     * Highest `z` among current elements (0 when there are none) — the base every
     * "lands frontmost" creation counts up from. Public because a multi-element
     * creation (the F-34 paste) has to stack its own copies relative to one
     * shared top INSIDE its single transaction, rather than re-reading it per
     * element through a create helper.
     */
    topZ() {
        let top = 0;
        getElements(this.doc).forEach((entry) => {
            const z = entry.get("z") ?? 0;
            if (z > top)
                top = z;
        });
        return top;
    }
    /* ---------------------------------------------------------------- */
    /* Connectors (F-38 — lines between elements)                        */
    /* ---------------------------------------------------------------- */
    /**
     * Create a connector between two element PORTS (F-38). `connectorId` is
     * client-generated and unique, so — like an element entry — the nested map is
     * created exactly once and never races a concurrent lazy creation.
     *
     * MANY-TO-MANY by construction: nothing here rejects a second connector on
     * the same port, the same pair of ports, or a loop back to the same element
     * on another side. Two clients drawing "the same" line simply get two
     * connectors with different ids — visually identical, individually deletable,
     * and convergent (there is no merge to get wrong).
     *
     * Endpoints carry no coordinates (see `ConnectorEndpoint`): the line's
     * geometry is derived from the elements' resolved rects at render time, so a
     * connected element that moves, resizes or rides a sort takes its lines with
     * it without a single extra write.
     */
    addConnector(connectorId, from, to, origin) {
        this.transact(() => {
            const connectors = getConnectors(this.doc);
            if (connectors.has(connectorId))
                return;
            const entry = new Y.Map();
            entry.set("from", { elementId: from.elementId, side: from.side });
            entry.set("to", { elementId: to.elementId, side: to.side });
            connectors.set(connectorId, entry);
        }, origin);
    }
    hasConnector(connectorId) {
        return getConnectors(this.doc).has(connectorId);
    }
    /** Undefined when the id is unknown OR either endpoint is malformed. */
    getConnector(connectorId) {
        const entry = getConnectors(this.doc).get(connectorId);
        if (!entry)
            return undefined;
        const from = entry.get("from");
        const to = entry.get("to");
        if (!isConnectorEndpoint(from) || !isConnectorEndpoint(to))
            return undefined;
        return { id: connectorId, from, to };
    }
    /**
     * Every structurally-valid connector, sorted by id — a total, replica-
     * identical order (lines have no z of their own; they paint beneath the
     * elements). A malformed entry is skipped, never thrown on: a doc is shared
     * state, and one bad entry must not take a reader down.
     *
     * Deliberately NOT filtered by endpoint existence — this layer knows nothing
     * about what a client has mounted, and a connector whose element is
     * momentarily unresolvable (pre-sync, mid-undo) must not vanish from the
     * accessor. Renderers filter on their own resolved rects; `sweepConnectors`
     * is the ONE place a missing element becomes a delete.
     */
    listConnectors() {
        const out = [];
        getConnectors(this.doc).forEach((_entry, id) => {
            const info = this.getConnector(id);
            if (info !== undefined)
                out.push(info);
        });
        out.sort((a, b) => (a.id < b.id ? -1 : a.id > b.id ? 1 : 0));
        return out;
    }
    /** Connectors with at least one end on `elementId` (either direction). */
    connectorsForElement(elementId) {
        return this.listConnectors().filter((c) => c.from.elementId === elementId || c.to.elementId === elementId);
    }
    /**
     * Re-point ONE end of a connector. `from`/`to` are whole-object keys, so an
     * element and its side move together, and concurrent re-points of the two
     * ends merge (different keys — C2); concurrent re-points of the SAME end are
     * plain LWW on one complete endpoint.
     */
    setConnectorEndpoint(connectorId, end, endpoint, origin) {
        this.transact(() => {
            getConnectors(this.doc)
                .get(connectorId)
                ?.set(end, { elementId: endpoint.elementId, side: endpoint.side });
        }, origin);
    }
    deleteConnector(connectorId, origin) {
        this.transact(() => {
            getConnectors(this.doc).delete(connectorId);
        }, origin);
    }
    /**
     * Delete every connector touching `elementId` — the cascade a client runs in
     * the SAME transaction as the element delete, so undo brings the element back
     * WITH its lines (one undo step) and no dangling line is ever synced. Returns
     * the ids removed.
     */
    deleteConnectorsForElement(elementId, origin) {
        const doomed = this.connectorsForElement(elementId).map((c) => c.id);
        if (doomed.length === 0)
            return [];
        this.transact(() => {
            const connectors = getConnectors(this.doc);
            for (const id of doomed)
                connectors.delete(id);
        }, origin);
        return doomed;
    }
    /**
     * C4 sweep: delete connectors whose endpoint element no longer exists — the
     * convergence net for the one case the cascade cannot cover, a connector
     * created concurrently with (or synced after) the deletion of an element it
     * lands on. Idempotent and re-checked INSIDE the transaction, so every client
     * observing the same dangling line races harmlessly to the same result.
     * Returns the ids actually removed.
     */
    sweepConnectors(origin) {
        const doomed = this.listConnectors()
            .filter((c) => !this.hasElement(c.from.elementId) || !this.hasElement(c.to.elementId))
            .map((c) => c.id);
        if (doomed.length === 0)
            return [];
        const removed = [];
        this.transact(() => {
            const connectors = getConnectors(this.doc);
            for (const id of doomed) {
                const info = this.getConnector(id);
                if (info === undefined)
                    continue; // a racing client already swept it
                if (this.hasElement(info.from.elementId) && this.hasElement(info.to.elementId))
                    continue;
                connectors.delete(id);
                removed.push(id);
            }
        }, origin);
        return removed;
    }
    /* ---------------------------------------------------------------- */
    /* Comment threads (F-50..F-56; docs/04 §2 schema, §3 anchoring)      */
    /* ---------------------------------------------------------------- */
    /**
     * Create a thread with its first message. No-op if the id exists (thread ids
     * are client-generated unique — there is no concurrent lazy-creation race,
     * which is why the nested per-thread map is safe here; see schema.ts).
     */
    createThread(threadId, anchor, first, origin) {
        this.transact(() => {
            const comments = this.commentsMap();
            if (comments.has(threadId))
                return;
            const entry = new Y.Map();
            entry.set("anchor", anchor);
            entry.set("resolved", false);
            entry.set("orphaned", false);
            const messages = new Y.Array();
            messages.push([{ ...first }]);
            entry.set("messages", messages);
            comments.set(threadId, entry);
        }, origin);
    }
    hasThread(threadId) {
        return this.commentsMap().has(threadId);
    }
    /**
     * Materialized read view of one thread, or undefined when it is gone.
     *
     * Messages are DE-DUPLICATED by id, keeping the first occurrence in Y.Array
     * order: two clients editing the SAME message concurrently each
     * delete-then-insert the entry, and both inserts survive the merge (Yjs
     * cannot know they are the same logical message). The array order is
     * deterministic and replica-identical, so this view is too.
     */
    getThread(threadId) {
        const entry = this.commentsMap().get(threadId);
        if (!entry)
            return undefined;
        const info = {
            id: threadId,
            anchor: entry.get("anchor"),
            resolved: entry.get("resolved") === true,
            orphaned: entry.get("orphaned") === true,
            messages: dedupeMessages(this.threadMessages(entry)),
        };
        const origAnchor = entry.get("origAnchor");
        if (origAnchor !== undefined)
            info.origAnchor = origAnchor;
        return info;
    }
    /** All threads, oldest first by first-message ts, ties broken by thread id. */
    listThreads() {
        const out = [];
        this.commentsMap().forEach((_entry, threadId) => {
            const info = this.getThread(threadId);
            if (info)
                out.push(info);
        });
        out.sort((a, b) => {
            const tsA = a.messages[0]?.ts ?? 0;
            const tsB = b.messages[0]?.ts ?? 0;
            if (tsA !== tsB)
                return tsA - tsB;
            return a.id < b.id ? -1 : a.id > b.id ? 1 : 0;
        });
        return out;
    }
    /** F-51 reply — append-only. False when the thread is gone (C4). */
    addMessage(threadId, message, origin) {
        const entry = this.commentsMap().get(threadId);
        if (!entry)
            return false;
        this.transact(() => {
            this.threadMessagesArray(entry)?.push([{ ...message }]);
        }, origin);
        return true;
    }
    /**
     * F-51 edit: replace the entry AT THE SAME INDEX (delete + insert in one
     * transaction — Yjs would not broadcast an in-place mutation of the stored
     * plain object), keeping id/author/ts and stamping `editedTs`. False when the
     * thread or message is gone.
     */
    editMessage(threadId, messageId, body, editedTs, origin) {
        const entry = this.commentsMap().get(threadId);
        if (!entry)
            return false;
        const messages = this.threadMessagesArray(entry);
        if (!messages)
            return false;
        const index = messages.toArray().findIndex((m) => m.id === messageId);
        if (index < 0)
            return false;
        const existing = messages.get(index);
        this.transact(() => {
            messages.delete(index, 1);
            messages.insert(index, [{ ...existing, body, editedTs }]);
        }, origin);
        return true;
    }
    /**
     * F-51 delete. Deleting the LAST message deletes the thread — an empty pin
     * has nothing to show. (Concurrent deletes of the last two messages each see
     * one survivor locally, so neither fires that rule and an empty thread
     * survives the merge; `deleteThreadIfEmpty` sweeps it.)
     */
    deleteMessage(threadId, messageId, origin) {
        const comments = this.commentsMap();
        const entry = comments.get(threadId);
        if (!entry)
            return false;
        const messages = this.threadMessagesArray(entry);
        if (!messages)
            return false;
        const index = messages.toArray().findIndex((m) => m.id === messageId);
        if (index < 0)
            return false;
        this.transact(() => {
            messages.delete(index, 1);
            if (messages.length === 0)
                comments.delete(threadId);
        }, origin);
        return true;
    }
    deleteThread(threadId, origin) {
        this.transact(() => {
            this.commentsMap().delete(threadId);
        }, origin);
    }
    /**
     * Sweep an EMPTY thread (the concurrent-last-two-deletes merge hole). The
     * emptiness is re-checked inside the transaction; racing sweeps from several
     * observing clients converge (delete is idempotent). Returns true iff this
     * call found an empty thread to remove.
     */
    deleteThreadIfEmpty(threadId, origin) {
        const comments = this.commentsMap();
        const entry = comments.get(threadId);
        if (!entry)
            return false;
        if ((this.threadMessagesArray(entry)?.length ?? 0) > 0)
            return false;
        this.transact(() => {
            comments.delete(threadId);
        }, origin);
        return true;
    }
    /**
     * F-52b: re-anchor a thread because the USER dragged its pin onto a
     * different element / cell / bare canvas. ONE whole-object `anchor` write —
     * `messages` is never read or written, so a concurrent reply merges
     * untouched (C2).
     *
     * It also clears the C4 orphan bookkeeping: an orphaned thread keeps
     * `origAnchor`, and the pins layer restores it the moment that target id
     * reappears (an undone deletion). Leaving the flags set would let that
     * restore silently yank the comment off the target the user just picked.
     *
     * Returns false, writing NOTHING, when the thread is gone (C4 — the delete
     * wins).
     */
    setThreadAnchor(threadId, anchor, origin) {
        const entry = this.commentsMap().get(threadId);
        if (!entry)
            return false;
        this.transact(() => {
            entry.set("anchor", anchor);
            if (entry.get("orphaned") === true)
                entry.set("orphaned", false);
            if (entry.has("origAnchor"))
                entry.delete("origAnchor");
        }, origin);
        return true;
    }
    /** F-53 resolve / re-open — a single boolean LWW key, merges with replies. */
    setResolved(threadId, resolved, origin) {
        this.transact(() => {
            this.commentsMap().get(threadId)?.set("resolved", resolved);
        }, origin);
    }
    /** C4 degradation flag — idempotent, so racing observers converge. */
    markOrphaned(threadId, origin) {
        this.transact(() => {
            this.commentsMap().get(threadId)?.set("orphaned", true);
        }, origin);
    }
    /**
     * C4 degradation (docs/04 §3): the anchor target was deleted → re-anchor to
     * the board at the pin's last resolved position, RECORDING the pre-
     * degradation anchor under `origAnchor` so a client that later observes the
     * target id reappear (the deletion undone) can re-attach via `restoreAnchor`.
     *
     * The original is recorded ONCE: a second, late degradation from another
     * observer must not overwrite it with the board anchor the first one wrote.
     */
    reanchorToBoard(threadId, position, origin) {
        this.transact(() => {
            const entry = this.commentsMap().get(threadId);
            if (!entry)
                return;
            if (!entry.has("origAnchor")) {
                const current = entry.get("anchor");
                if (current !== undefined)
                    entry.set("origAnchor", current);
            }
            entry.set("anchor", { kind: "board", x: position.x, y: position.y });
        }, origin);
    }
    /**
     * Reverse of the C4 degradation: the recorded original anchor's target
     * exists again, so re-attach and clear the flag. A flag-only orphan (no
     * recorded original — the target died before any layout pass resolved a pin
     * position) just has its flag cleared; the anchor is left alone.
     */
    restoreAnchor(threadId, origin) {
        this.transact(() => {
            const entry = this.commentsMap().get(threadId);
            if (!entry)
                return;
            const orig = entry.get("origAnchor");
            if (orig !== undefined) {
                entry.set("anchor", orig);
                entry.delete("origAnchor");
            }
            if (entry.get("orphaned") === true)
                entry.set("orphaned", false);
        }, origin);
    }
    /* ---------------------------------------------------------------- */
    /* Internals                                                         */
    /* ---------------------------------------------------------------- */
    metaMap() {
        return getMeta(this.doc);
    }
    /** Comments root — the comment accessors above are its only users. */
    commentsMap() {
        return getComments(this.doc);
    }
    /** Design root — the design accessors above are its only users. */
    designMap() {
        return getDesign(this.doc);
    }
    thumbsMap() {
        return getThumbs(this.doc);
    }
    /** One optional string setting on the thumbs map; `undefined` deletes it. */
    setThumbSetting(key, value, origin) {
        const thumbs = this.thumbsMap();
        this.transact(() => {
            if (value === undefined || value === "")
                thumbs.delete(key);
            else
                thumbs.set(key, value);
        }, origin);
    }
    thumbSetting(key) {
        const raw = this.thumbsMap().get(key);
        return typeof raw === "string" && raw.length > 0 ? raw : undefined;
    }
    /** `(id, ord)` pairs for the ordering helpers, skipping half-written entries. */
    orderedRefEntries(refs) {
        return ordEntriesOf(refs);
    }
    orderedShotEntries(shots) {
        return ordEntriesOf(shots);
    }
    threadMessagesArray(entry) {
        const messages = entry.get("messages");
        return messages instanceof Y.Array ? messages : undefined;
    }
    threadMessages(entry) {
        return this.threadMessagesArray(entry)?.toArray() ?? [];
    }
    /**
     * F-28 insert clamp: an index at or before the locked source column's slot
     * becomes the slot just right of it. `ordered` is the CURRENT column order,
     * passed in so the caller's single read is reused.
     */
    clampColumnIndex(sheetId, ordered, index) {
        const sourceColId = this.sourceColumnId(sheetId);
        if (sourceColId === undefined)
            return index; // pre-F-28 board: nothing to protect
        const sourceIndex = ordered.findIndex((e) => e.id === sourceColId);
        if (sourceIndex < 0)
            return index;
        return Math.max(index, sourceIndex + 1);
    }
    /** Single-key write on a column's nested meta map (C2). */
    setColumnKey(sheetId, colId, key, value, origin) {
        this.transact(() => {
            getColMeta(this.doc, sheetId).get(colId)?.set(key, value);
        }, origin);
    }
    /**
     * Write ONE format property's flat key (C2). `undefined` = clear, written as
     * an explicit `null` so it MASKS a legacy whole-object `fmt` entry — but only
     * when there is something to mask, so clearing an already-clear cell stays a
     * true no-op.
     */
    setCellFormatProp(sheetId, rowId, colId, prop, value, origin) {
        const cells = getCells(this.doc, sheetId);
        const key = cellPropKey(rowId, colId, prop);
        if (value === undefined && !cells.has(key) && !cells.has(legacyCellFmtKey(rowId, colId))) {
            return;
        }
        this.transact(() => {
            cells.set(key, value ?? null);
        }, origin);
    }
    /**
     * Resolve ONE format property: the per-property key wins; `null` means
     * "explicitly none" and masks the legacy object; an ABSENT key falls through
     * to the retired whole-object `fmt` entry (read-compat shim — schema.ts).
     */
    readCellFormatProp(sheetId, rowId, colId, prop) {
        const cells = getCells(this.doc, sheetId);
        const raw = cells.get(cellPropKey(rowId, colId, prop));
        if (raw === null)
            return undefined; // explicitly none — masks the legacy value
        if (raw !== undefined)
            return raw;
        const legacy = cells.get(legacyCellFmtKey(rowId, colId));
        if (typeof legacy !== "object" || legacy === null)
            return undefined;
        return legacy[prop];
    }
    /**
     * DERIVED aggregate format view (never a stored value since the 2026-07-13
     * `fmt` retirement): every property resolved through `readCellFormatProp`.
     * `undefined` when the cell carries no format at all — which is what lets
     * `getCell` report a formatless, valueless cell as absent.
     */
    readCellFormat(sheetId, rowId, colId) {
        const out = {};
        let any = false;
        for (const prop of CELL_FORMAT_PROPS) {
            const value = this.readCellFormatProp(sheetId, rowId, colId, prop);
            if (value === undefined)
                continue;
            assignCellFormatProp(out, prop, value);
            any = true;
        }
        return any ? out : undefined;
    }
    /** Entries of a meta map as `{id, ord}` sorted by `(ord, id)`. */
    orderedEntries(map) {
        const entries = [];
        map.forEach((entry, id) => {
            entries.push({ id, ord: entry.get("ord") ?? "" });
        });
        return sortOrdered(entries);
    }
}
/**
 * Plain-object copy of a mark on its way into the doc. The caller's array may
 * be derived from what is already stored (`applyMarkRange` reads, edits and
 * hands back), and a Yjs map value must be plain JSON this code still owns —
 * never an object the caller keeps a reference to and might mutate later.
 */
function cloneMark(mark) {
    const out = { start: mark.start, end: mark.end, quote: mark.quote };
    if (mark.color !== undefined)
        out.color = mark.color;
    return out;
}
/** Store a fresh plain object per switch — same reason as `cloneMark`. */
function cloneSwitch(sw) {
    const out = { at: sw.at, quote: sw.quote };
    if (sw.ty !== undefined)
        out.ty = sw.ty;
    return out;
}
/**
 * Copy only the DEFINED fields of a link-card payload (F-31). Every descriptive
 * field is optional — skeleton, each fallback rung, and the video card differ by
 * which are present — so a blind spread would bake `undefined`-valued keys into
 * the doc.
 */
function normalizeLinkCardPayload(payload) {
    const out = {
        kind: "linkCard",
        url: payload.url,
        status: payload.status,
    };
    if (payload.title !== undefined)
        out.title = payload.title;
    if (payload.description !== undefined)
        out.description = payload.description;
    if (payload.thumbnailAssetId !== undefined)
        out.thumbnailAssetId = payload.thumbnailAssetId;
    if (payload.favicon !== undefined)
        out.favicon = payload.favicon;
    if (payload.domain !== undefined)
        out.domain = payload.domain;
    if (payload.media !== undefined)
        out.media = payload.media;
    if (payload.provider !== undefined)
        out.provider = payload.provider;
    if (payload.thumbW !== undefined)
        out.thumbW = payload.thumbW;
    if (payload.thumbH !== undefined)
        out.thumbH = payload.thumbH;
    // F-138: the clip range, normalized (a copy created from a card that has
    // one — the F-34 paste — keeps the range the writer set).
    const clip = clipRangeOf({ ...out, clipStartMs: payload.clipStartMs, clipEndMs: payload.clipEndMs });
    if (clip?.startMs !== undefined)
        out.clipStartMs = clip.startMs;
    if (clip?.endMs !== undefined)
        out.clipEndMs = clip.endMs;
    return out;
}
/** Narrow the prop/value pair back together for the derived `CellFormat` view. */
function assignCellFormatProp(target, prop, value) {
    switch (prop) {
        case "bold":
        case "italic":
        case "underline":
        case "strike":
            target[prop] = value;
            return;
        case "color":
        case "bg":
            target[prop] = value;
            return;
        case "align":
            target.align = value;
            return;
    }
}
/** First occurrence of each message id, in stored (replica-identical) order. */
function dedupeMessages(messages) {
    const seen = new Set();
    const out = [];
    for (const message of messages) {
        if (seen.has(message.id))
            continue;
        seen.add(message.id);
        out.push(message);
    }
    return out;
}
/**
 * `(id, ord)` pairs from a nested per-entity map, for the C1 ordering helpers.
 * An entry with no string `ord` (half-written, or a future writer's) sorts as
 * `""` rather than being dropped — losing a user's uploaded reference because
 * one key arrived late would be worse than showing it first.
 */
function ordEntriesOf(map) {
    const out = [];
    for (const [id, entry] of map) {
        const ord = entry.get("ord");
        out.push({ id, ord: typeof ord === "string" ? ord : "" });
    }
    return out;
}
function numberOr(value, fallback) {
    return typeof value === "number" && Number.isFinite(value) ? value : fallback;
}
/** `#rgb` / `#rrggbb`, case-insensitive — what a renderer can hand to CSS. */
const HEX_COLOUR = /^#(?:[0-9a-f]{3}|[0-9a-f]{6})$/i;
/**
 * Sanitize a palette before it is stored: trim, lowercase the hex, drop any
 * swatch whose colour is not a hex value, and cap the list.
 *
 * Dropping rather than repairing is deliberate — the palette's whole job is to
 * be a set of colours a person can hand to an animator, and a model that
 * returned "warm sand" instead of `#e8d9c0` has not given us one. Storing that
 * string would render as a transparent swatch nobody can pick up.
 */
function normalizePalette(swatches) {
    const out = [];
    for (const swatch of swatches) {
        const hex = swatch.hex.trim().toLowerCase();
        if (!HEX_COLOUR.test(hex))
            continue;
        out.push({ hex, name: swatch.name.trim() });
        if (out.length >= MAX_PALETTE_SWATCHES)
            break;
    }
    return out;
}
/**
 * Update a Y.Text in place to read `next`, as a minimal common-prefix/suffix
 * splice (C7). Whole-value replacement of the shared type would discard
 * concurrent character edits from other replicas; a splice keeps them.
 */
function spliceYText(text, next) {
    const current = text.toString();
    if (current === next)
        return;
    let start = 0;
    const minLen = Math.min(current.length, next.length);
    while (start < minLen && current[start] === next[start])
        start++;
    let endCurrent = current.length;
    let endNext = next.length;
    while (endCurrent > start && endNext > start && current[endCurrent - 1] === next[endNext - 1]) {
        endCurrent--;
        endNext--;
    }
    if (endCurrent > start)
        text.delete(start, endCurrent - start);
    if (endNext > start)
        text.insert(start, next.slice(start, endNext));
}
/** Exactly the fields a stem's `source` uses — see `BoardDoc.setCellBgm`. */
function normalizeBgmStem(stem) {
    const out = { source: stem.source, title: stem.title };
    if (stem.source === "url" && stem.url !== undefined)
        out.url = stem.url;
    if (stem.source === "asset" && stem.assetId !== undefined)
        out.assetId = stem.assetId;
    if (stem.durationMs !== undefined)
        out.durationMs = stem.durationMs;
    return out;
}
//# sourceMappingURL=board.js.map